/**
 * DEMO MODE
 * ----------------------------------------------------------------------------
 * When a specific demo account (celebritydrapes@gmail.com) logs in, the app
 * serves fully static data from the `demo-data/` folder instead of hitting the
 * real backend. This lets us showcase every dashboard (Scaleboard, Sights,
 * Alerts, Notifications, Profile/Team, Integrations) with realistic numbers
 * without a live store.
 *
 * How it works:
 *  1. On login (AuthContext / SignInForm), if the email matches the demo email,
 *     we flag demo mode in localStorage and hydrate demo user + stores.
 *  2. The axios request interceptor (src/lib/axios.ts) calls `resolveDemoResponse`
 *     for every request. If demo mode is on and the URL maps to a demo file, the
 *     request is short-circuited and the JSON is returned directly (never touches
 *     the network).
 *  3. The Scaleboard (Intelligence.tsx) data arrives via socket.io, not axios,
 *     so that page injects the demo CEO report directly when demo mode is on.
 *
 * IMPORTANT: All of this is completely inert for every other user. `isDemoMode()`
 * gates every branch, so real accounts behave exactly as before.
 */

export const DEMO_EMAIL = "celebritydrapes@gmail.com";
/** Fixed password the demo account must use in the password login flow. */
export const DEMO_PASSWORD = "Demo1234!";
const DEMO_FLAG_KEY = "netsights_demo_mode";

/** Eagerly load every JSON file in the demo-data folder (Vite root-relative). */
const demoFiles = import.meta.glob("/demo-data/*.json", {
  eager: true,
  import: "default",
}) as Record<string, any>;

/** Look up a demo file by its base name, e.g. "me" -> demo-data/me.json */
function getDemoFile<T = any>(name: string): T | null {
  const key = `/demo-data/${name}.json`;
  const found = demoFiles[key];
  return found != null ? (found as T) : null;
}

export function isDemoEmail(email?: string | null): boolean {
  return (email ?? "").trim().toLowerCase() === DEMO_EMAIL;
}

// ── LocalStorage-backed demo persistence ─────────────────────────────────────
// Add/edit/delete for team members and alerts persist to localStorage so the
// demo feels real across the session. Seeded from the demo JSON on first read.
function lsReadList<T = any>(key: string, seed: () => T[]): T[] {
  if (typeof window === "undefined") return seed();
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const initial = seed();
  try { localStorage.setItem(key, JSON.stringify(initial)); } catch { /* ignore */ }
  return initial;
}

function lsWriteList<T = any>(key: string, list: T[]): void {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* ignore */ }
}

const DEMO_MEMBERS_KEY = "netsights_demo_members";
const DEMO_ALERTS_KEY = "netsights_demo_alerts";
/** Team members shown in TeamTable (Settings → Members). CRUD, unlimited, local. */
const DEMO_TEAM_KEY = "netsights_demo_team";
/** Custom sights created by the demo user. Seeded from sights-list.json on first read. */
const DEMO_SIGHTS_KEY = "netsights_demo_sights";
// Persisted selected sight widgets (Manage Cards). Keyed per sight id so each
// dashboard remembers its own selection across the demo session.
const DEMO_SIGHT_WIDGETS_KEY = "netsights_demo_sight_widgets_v2";

/** Plan the demo user has "activated" via Try Now / Activate (persisted locally). */
const DEMO_SELECTED_PLAN_KEY = "netsights_demo_selected_plan";

function getDemoSelectedPlanKey(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(DEMO_SELECTED_PLAN_KEY);
  } catch {
    return null;
  }
}

function setDemoSelectedPlanKey(key: string): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(DEMO_SELECTED_PLAN_KEY, key);
  } catch { /* ignore */ }
}

function parseBody(body: any): any {
  if (!body) return {};
  if (typeof body === "string") {
    try { return JSON.parse(body); } catch { return {}; }
  }
  return body;
}

// ── Demo Sight widgets: persist Manage-Cards selection to localStorage ───────
// The Manage Cards modal POSTs { shop, widgets: [{widget_id,is_enabled,position,
// settings,is_initial_selection}] } to /sights/:id/widgets. We store the enabled
// entries, then rebuild the grouped `widgets` object (by category) that the sight
// dashboard reads, looking up full widget defs from widgets-available.json.
type DemoSelectedWidget = {
  widget_id: number;
  position?: number;
  settings?: any;
  is_initial_selection?: boolean;
  has_prediction?: boolean;
};

function readSightWidgetMap(): Record<string, DemoSelectedWidget[]> {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(DEMO_SIGHT_WIDGETS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {};
}

function writeSightWidgetMap(map: Record<string, DemoSelectedWidget[]>): void {
  if (typeof window === "undefined") return;
  try { localStorage.setItem(DEMO_SIGHT_WIDGETS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

/** Flatten every widget definition from widgets-available.json into an id→def map. */
function getWidgetDefById(): Record<number, any> {
  const avail = getDemoFile("widgets-available");
  const groups = avail?.widgets ?? {};
  const map: Record<number, any> = {};
  Object.values(groups).forEach((list: any) => {
    (list || []).forEach((wdef: any) => { if (wdef?.id != null) map[wdef.id] = wdef; });
  });
  return map;
}

function getWidgetGroups(): Record<string, any[]> {
  return (getDemoFile("widgets-available")?.widgets ?? {}) as Record<string, any[]>;
}

function flattenWidgetDefs(): any[] {
  return Object.values(getWidgetGroups()).flat();
}

function layoutWidgetDefs(defs: any[]): any[] {
  return defs.map((def, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    return {
      ...def,
      pivot: {
        is_enabled: true,
        position: i,
        settings: { width: 4, height: 2, x: col * 4, y: row * 2 },
        is_initial_selection: i < 3 || !!def.priority_card,
        has_prediction: !!def.priority_card,
        is_initial_prediction: i === 0,
      },
    };
  });
}

function groupWidgetsByCategory(widgets: any[]): Record<string, any[]> {
  const grouped: Record<string, any[]> = {};
  widgets.forEach((w) => {
    const cat = w.category || "shopify";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(w);
  });
  return grouped;
}

type DemoSightMeta = {
  id: number;
  name: string;
  slug: string;
  md5_hash: string;
  icon: string;
  description: string;
  category: string;
};

function getDemoSightCatalog(): DemoSightMeta[] {
  return [
    { id: 1, name: "Netboard", slug: "netboard", md5_hash: "a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6", icon: "grid", description: "Your main analytics dashboard", category: "all" },
    { id: 2, name: "Shopify", slug: "shopify", md5_hash: "c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9", icon: "shopping-bag", description: "Store sales, customers and inventory", category: "shopify" },
    { id: 3, name: "Meta Ads", slug: "meta-ads", md5_hash: "d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0", icon: "trending-up", description: "Meta advertising performance", category: "meta_ads" },
    { id: 4, name: "Google Analytics", slug: "ga4", md5_hash: "e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1", icon: "bar-chart", description: "GA4 traffic and conversion", category: "ga4" },
    { id: 5, name: "Google Ads", slug: "google-ads", md5_hash: "f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2", icon: "megaphone", description: "Google Ads spend and ROAS", category: "google_ads" },
    { id: 6, name: "Search Console", slug: "search-console", md5_hash: "a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3", icon: "search", description: "Organic search impressions and clicks", category: "gsc" },
    { id: 7, name: "Blended", slug: "blended", md5_hash: "b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4", icon: "layers", description: "Blended marketing efficiency", category: "blended" },
    { id: 8, name: "Payments", slug: "payments", md5_hash: "c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5", icon: "credit-card", description: "Captured, failed and refunded payments", category: "payments" },
  ];
}

function findDemoSightMeta(id: string): DemoSightMeta {
  const catalog = getDemoSightCatalog();
  const key = String(id);
  return (
    catalog.find((s) => String(s.id) === key || s.slug === key || s.md5_hash === key) ||
    catalog[0]
  );
}

function defsForSight(meta: DemoSightMeta): any[] {
  const groups = getWidgetGroups();
  if (meta.category === "all") return flattenWidgetDefs();
  return groups[meta.category] || [];
}

function buildSightPayload(meta: DemoSightMeta, widgets: any[]): any {
  const grouped = groupWidgetsByCategory(widgets);
  const categories = Object.keys(grouped);
  const avail = getDemoFile("widgets-available");
  return {
    sight: {
      id: meta.id,
      name: meta.name,
      slug: meta.slug,
      md5_hash: meta.md5_hash,
      icon: meta.icon,
      description: meta.description,
    },
    widgets: grouped,
    categories,
    plan_info: {
      ...(avail?.plan_info || {}),
      widget_limit: -1,
      widget_limit_per_connection: -1,
      total_widgets: widgets.length,
    },
  };
}

function getDemoSightsList(): any {
  const groups = getWidgetGroups();
  return {
    sights: getDemoSightCatalog().map((s, i) => ({
      id: s.id,
      name: s.name,
      slug: s.slug,
      md5_hash: s.md5_hash,
      icon: s.icon,
      description: s.description,
      order: i + 1,
      widget_count: s.category === "all" ? flattenWidgetDefs().length : (groups[s.category] || []).length,
    })),
  };
}

/**
 * Build a /sights/:id response for demo mode. Uses the persisted selection when
 * present, otherwise returns every widget for that sight (netboard = all
 * integrations; each integration sight = that connection's widgets).
 */
function buildDemoSight(sightId: string): any {
  const meta = findDemoSightMeta(sightId);
  const map = readSightWidgetMap();
  const saved = map[String(sightId)] || map[meta.slug] || map[meta.md5_hash] || (meta.slug === "netboard" ? map["default"] : undefined);

  if (saved && saved.length) {
    const defById = getWidgetDefById();
    const widgets = saved
      .map((sel, i) => {
        const def = defById[sel.widget_id];
        if (!def) return null;
        const col = i % 3;
        const row = Math.floor(i / 3);
        return {
          ...def,
          pivot: {
            is_enabled: true,
            position: sel.position ?? i,
            settings: sel.settings ?? { width: 4, height: 2, x: col * 4, y: row * 2 },
            is_initial_selection: !!sel.is_initial_selection,
            has_prediction: !!sel.has_prediction,
            is_initial_prediction: false,
          },
        };
      })
      .filter(Boolean);
    return buildSightPayload(meta, widgets);
  }

  return buildSightPayload(meta, layoutWidgetDefs(defsForSight(meta)));
}

/** Persist the Manage-Cards selection for a sight (called on POST /sights/:id/widgets). */
function saveDemoSightWidgets(sightId: string, body: any): void {
  const b = parseBody(body);
  const widgets: any[] = Array.isArray(b?.widgets) ? b.widgets : [];
  const enabled: DemoSelectedWidget[] = widgets
    .filter((w) => w && w.is_enabled !== false && w.widget_id != null)
    .map((w) => ({
      widget_id: Number(w.widget_id),
      position: w.position,
      settings: w.settings,
      is_initial_selection: !!w.is_initial_selection,
      has_prediction: !!w.has_prediction,
    }));
  const map = readSightWidgetMap();
  map[String(sightId)] = enabled;
  const meta = findDemoSightMeta(sightId);
  map[meta.slug] = enabled;
  map[meta.md5_hash] = enabled;
  if (meta.slug === "netboard") map["default"] = enabled;
  writeSightWidgetMap(map);
  // Update widget_count on the persisted sight so the sidebar badge stays in sync.
  try {
    const sights = lsReadList<any>(DEMO_SIGHTS_KEY, () => getDemoFile("sights-list")?.sights ?? []);
    const updated = sights.map((s: any) =>
      String(s.id) === String(sightId) || s.slug === sightId || s.md5_hash === sightId
        ? { ...s, widget_count: enabled.length }
        : s
    );
    // Also update "default" sight (id=1) if sightId is the default netboard slug.
    lsWriteList(DEMO_SIGHTS_KEY, updated);
  } catch { /* ignore */ }
}

/** True when the currently logged-in session is the demo account. */
export function isDemoMode(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(DEMO_FLAG_KEY) === "true";
}

export function enableDemoMode(): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(DEMO_FLAG_KEY, "true");
  installDemoFetchPatch();
}

export function disableDemoMode(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem(DEMO_FLAG_KEY);
}

const DEMO_PROFILE_KEY = "netsights_demo_profile";

/** The demo user payload (matches the shape of /me and /login responses). */
export function getDemoMe(): any {
  const base = getDemoFile("me");
  if (typeof window === "undefined") return base;
  try {
    const raw = localStorage.getItem(DEMO_PROFILE_KEY);
    if (raw) {
      const override = JSON.parse(raw);
      return { ...base, ...override };
    }
  } catch { /* ignore */ }
  return base;
}

/** Demo stores array (shape of GET /api/stores) - includes full permissions. */
export function getDemoStores(): any[] {
  const s = getDemoFile("stores");
  return Array.isArray(s) ? s : [];
}

const GBP = "£";

/** Compact GBP formatter for demo card values (e.g. £1.2M, £42K, £980). */
function fmtDemoCurrency(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}${GBP}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${GBP}${(abs / 1_000).toFixed(0)}K`;
  return `${sign}${GBP}${Math.round(abs).toLocaleString("en-GB")}`;
}

function fmtDemoValue(value: number | string, format?: string): string {
  const numValue = typeof value === 'number' ? value : Number(value);
  switch (format) {
    case "currency":
      return fmtDemoCurrency(numValue);
    case "percentage":
      return `${value}%`;
    case "decimal":
      return numValue.toFixed(2);
    case "seconds":
      return `${value}s`;
    case "score":
      return `${value}`;
    case "text":
      return String(value);
    default:
      return Number.isFinite(numValue) ? numValue.toLocaleString("en-GB") : String(value);
  }
}

function fmtDemoChange(changePct?: number): string {
  if (changePct == null || changePct === 0) return "";
  const arrow = changePct > 0 ? "↑" : "↓";
  return `${arrow} ${Math.abs(changePct).toFixed(1)}%`;
}

/**
 * Transform a raw demo section card (shape: {id,title,value,previousValue,
 * change,changePct,format,status,category}) into the richer card shape that
 * KPIDashboard's `getNormalizedSections` + KPICard expect
 * (metric, data{value,change,subtext,timeframe}, comparisons, diagnosis, ...).
 */
function transformDemoCard(card: any): any {
  const value = fmtDemoValue(card.value, card.format);
  const change = fmtDemoChange(card.changePct);
  const prev = fmtDemoValue(card.previousValue, card.format);
  const statusText =
    card.status === "healthy"
      ? "Healthy"
      : card.status === "warning"
      ? "Watch"
      : card.status === "critical"
      ? "Critical"
      : card.status === "positive"
      ? "Growing"
      : "Info";
  const diagType =
    card.status === "critical" || card.status === "warning" ? "wrong" : "check";

  // Use the new fields if available
  const description = card.description || `${card.title} is ${value}`;
  const benchmark = card.benchmark || "-";
  const details = card.details || "";
  const actions = card.actions || ["Take Action"];

  return {
    id: card.id,
    metric: card.title,
    label: card.title,
    status: card.status || "info",
    statusText,
    data: {
      timeframe: "Yesterday",
      value,
      change,
      subtext: description,
    },
    comparisons: [
      { label: "Previous", value: prev },
      { label: "Change", value: change || "0%" },
      { label: "Benchmark", value: benchmark },
    ],
    diagnosis: [
      {
        type: diagType,
        text: details || (diagType === "check"
          ? `${card.title} is trending in a healthy range.`
          : `${card.title} needs attention this period.`),
      },
    ],
    primaryCTA: { label: actions[0] || "Take Action" },
    format: card.format,
    benchmark: benchmark,
    details: details,
    actions: actions,
  };
}

type DemoDiag = [type: "check" | "wrong" | "critical", text: string];

const PERIOD_LABELS: Record<string, string> = {
  yesterday: "Yesterday",
  last_7_days: "Last 7 Days",
  this_week: "This Week",
  last_week: "Last Week",
  this_month: "This Month",
  last_month: "Last Month",
};

// Metrics that are SUMS over the period (revenue, orders, sessions...) -
// these scale up a lot when the period gets longer.
const ADDITIVE_MULTIPLIER: Record<string, number> = {
  yesterday: 1,
  last_7_days: 6.4,
  this_week: 3.8,
  last_week: 7,
  this_month: 27,
  last_month: 30,
};

// Metrics that are RATES/AVERAGES/RATIOS (ROAS, %, CAC...) - only a small
// realistic wobble, never a big multiply.
const RATE_MULTIPLIER: Record<string, number> = {
  yesterday: 1,
  last_7_days: 1.06,
  this_week: 0.96,
  last_week: 1.03,
  this_month: 1.11,
  last_month: 1.08,
};

// Card ids whose main value is a running total (not a rate/ratio/average).
const ADDITIVE_CARD_IDS = new Set([
  "revenue",
  "daily_ad_spend",
  "orders_count",
  "blended_revenue",
  "meta_revenue",
  "google_revenue",
  "meta_spend",
  "google_spend",
  "blended_spend",
  "cpm_trend",
  "rzp_total",
  "winback_revenue_potential",
]);

function withCommas(n: number): string {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

// Scales every number found inside a formatted string ("£110,805", "3.27x",
// "31.64%", "70% profitable") by `factor`, keeping symbols/suffixes and the
// original decimal precision intact.
function scaleDemoValue(str: string, factor: number): string {
  if (typeof str !== "string") return str;
  return str.replace(/[\d,]+\.?\d*/g, (match) => {
    const hasComma = match.includes(",");
    const cleaned = match.replace(/,/g, "");
    const num = parseFloat(cleaned);
    if (isNaN(num)) return match;
    const scaled = num * factor;
    const decimalPart = cleaned.split(".")[1];
    if (decimalPart) return scaled.toFixed(decimalPart.length);
    const rounded = Math.round(scaled);
    return hasComma || rounded >= 1000 ? withCommas(rounded) : String(rounded);
  });
}

function kpiCard(opts: {
  id: string;
  metric: string;
  value: string;
  change?: string;
  status: string;
  statusText: string;
  subtext: string;
  prev: string;
  bench: string;
  diags: DemoDiag[];
  cta: string;
  extra?: Record<string, any>;
}): any {
  return {
    id: opts.id,
    metric: opts.metric,
    status: opts.status,
    statusText: opts.statusText,
    data: {
      timeframe: "Yesterday",
      value: opts.value,
      change: opts.change || "",
      subtext: opts.subtext,
    },
    comparisons: [
      { label: "Day Ref Period", value: opts.prev },
      { label: "Benchmark", value: opts.bench },
    ],
    diagnosis: opts.diags.map(([type, text]) => ({ type, text })),
    primaryCTA: { label: opts.cta },
    ...(opts.extra || {}),
  };
}

/**
 * Full Scaleboard (Marketing / Product / Customer / Store / iSight AI / Payment)
 * matching the live Celebrity Drapes board, already in KPICard shape.
 */
function buildDemoScaleboardSections(): Record<string, { title: string; cards: any[] }> {
  return {
    marketing: {
      title: "Marketing",
      cards: [
        kpiCard({
          id: "revenue",
          metric: "Revenue",
          value: "£110,805",
          change: "↑ 54.8%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Total revenue fell 50% to £71,555, driven by a 50% drop in website orders.",
          prev: "£71,555",
          bench: "£100K",
          diags: [
            ["check", "Revenue dropped from £140K to £71,555, driven by 50% fewer website orders."],
            ["check", "Conversion rate holding at 7.3%."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "blended_roas",
          metric: "Blended ROAS",
          value: "3.27x",
          change: "↑ 8.4%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Combined ROAS is 3.27x vs 3.0x target",
          prev: "3.0x",
          bench: "3.0x",
          diags: [
            ["wrong", "Your ad spend is growing 104% faster than revenue. Your CAC rose while AOV stayed the same."],
            ["wrong", "Variable costs (wages, COGS, shipping) ate 79% of every sale."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "mer",
          metric: "Marketing Efficiency Ratio",
          value: "31.64%",
          change: "↑ 19.7%",
          status: "warning",
          statusText: "Watch",
          subtext: "MER is 31.64% vs <25% target - Watch",
          prev: "26.17%",
          bench: "<25%",
          diags: [
            ["wrong", "Marketing Efficiency Ratio worsened 28% to 31.6%, meaning advertising spend rose faster than revenue."],
            ["wrong", "Variable costs (wages, COGS, shipping) ate 79% of every sale."],
          ],
          cta: "Fix",
        }),
        kpiCard({
          id: "campaign_efficiency",
          metric: "Campaigns over target",
          value: "70% profitable",
          change: "↑ 4%",
          status: "warning",
          statusText: "Watch",
          subtext: "70% of 10 active campaigns beating target",
          prev: "66% profitable",
          bench: "75%",
          diags: [
            ["check", "Your campaign mix is 70% profitable vs 75% target - 30% of spend is wasted on underperforming campaigns."],
            ["wrong", "3 campaigns burning 30% of total ad spend with 0% ROAS."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "ad_spend_vs_budget",
          metric: "Ad spend vs budget",
          value: "31% of plan",
          change: "↓ 2.8%",
          status: "warning",
          statusText: "Watch",
          subtext: "MTD spend is 31% of monthly budget",
          prev: "43% of plan",
          bench: "100% of plan",
          diags: [
            ["check", "Your month-to-date budget utilisation sits at 31% vs 43% expected, so you are pacing behind."],
            ["wrong", "Underpacing - review spend vs budgets."],
          ],
          cta: "Adjust",
        }),
        kpiCard({
          id: "google_roas",
          metric: "Google Ads ROAS",
          value: "1.34x",
          change: "↓ 16.3%",
          status: "critical",
          statusText: "Critical",
          subtext: "Google Ads ROAS is 1.34x vs 2.8x target",
          prev: "1.6x",
          bench: "2.8x",
          diags: [
            ["wrong", "Google Ads ROAS fell 16% to 1.34x, indicating low profitability vs spend."],
            ["wrong", "Review search terms and match types."],
          ],
          cta: "Optimise",
        }),
        kpiCard({
          id: "blended_cac",
          metric: "LTV:CAC",
          value: "£830",
          change: "↓ 11.8%",
          status: "warning",
          statusText: "Watch",
          subtext: "CAC is £830 vs £1,200 target - Watch",
          prev: "£940",
          bench: "£1,200",
          diags: [
            ["check", "Your full funnel campaign structure on Meta is delivering consistent contribution and a strong blended CAC."],
            ["wrong", "Broader audience or lookalike expansion could lower CAC."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "ad_frequency",
          metric: "Ad Frequency",
          value: "1.7x",
          change: "↑ 6.3%",
          status: "warning",
          statusText: "Watch",
          subtext: "Frequency is 1.7x vs 2.5x fatigue ceiling",
          prev: "1.6x",
          bench: "2.5x",
          diags: [
            ["check", "Ad frequency sitting at 1.7x, staying below the fatigue ceiling."],
            ["wrong", "Refresh creatives and expand audiences immediately."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "click_to_purchase",
          metric: "Click-to-Purchase Rate",
          value: "0.8%",
          change: "↓ 20%",
          status: "critical",
          statusText: "Critical",
          subtext: "0.8% of clicks convert to purchase - landing page friction",
          prev: "1.0%",
          bench: "2.5%",
          diags: [
            ["wrong", "Only 0.8% of ad clicks convert to purchase vs 2.5% target."],
            ["wrong", "Optimise landing pages and PDP CTAs."],
          ],
          cta: "Fix",
        }),
        kpiCard({
          id: "paid_organic_mix",
          metric: "Paid vs Organic Mix",
          value: "44% paid",
          change: "↓ 12.5%",
          status: "warning",
          statusText: "Watch",
          subtext: "Paid mix is 44% vs 35% target - High CAC risk",
          prev: "50% paid",
          bench: "35%",
          diags: [
            ["check", "Your paid-to-organic mix is 44% paid vs 35% target - heavy paid reliance will undermine LTV if CAC stays elevated."],
            ["wrong", "Organic share declining - invest in SEO."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "email_whatsapp_ctr",
          metric: "Email ranking",
          value: "5",
          change: "↑ 25%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "CTR is 5% vs 6%+ target - High CTR",
          prev: "4",
          bench: "6+",
          diags: [
            ["check", "Email/WhatsApp CTR is 5%, indicating a strong list vs 6%+ target."],
          ],
          cta: "Grow Channel",
        }),
        kpiCard({
          id: "channel_roi",
          metric: "Organic ranking",
          value: "3.3x ranking",
          change: "↑ 14.8%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Meta #1, Google #2, Email #3 - Best mix",
          prev: "2.9x ranking",
          bench: "3.0x ranking",
          diags: [
            ["check", "Channel ROI ranking led by Meta at 3.3x."],
          ],
          cta: "View Account",
        }),
        kpiCard({
          id: "daily_ad_spend",
          metric: "Daily Ad Spend",
          value: "£6,286",
          change: "↑ 8.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Daily ad spend is £6,286 vs £5K target - Watch",
          prev: "£5,793",
          bench: "£5K",
          diags: [
            ["check", "Your CAC is extending slightly as you scale spend into a healthy range."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "blended_cpc",
          metric: "Blended CPC",
          value: "£4",
          change: "↑ 0%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Blended CPC is £4 vs £3 target",
          prev: "£4",
          bench: "£3",
          diags: [
            ["check", "Your ad frequency and paid mix remain strong as you scale spend."],
          ],
          cta: "Review CPC",
        }),
        kpiCard({
          id: "meta_ctr",
          metric: "Meta CTR",
          value: "7.96%",
          change: "↑ 1.5%",
          status: "critical",
          statusText: "Critical",
          subtext: "Meta CTR is 7.96% vs 1.5% target",
          prev: "7.84%",
          bench: "1.5%",
          diags: [
            ["check", "Conversion-quality CTR is 7.96% vs 1.5% target."],
          ],
          cta: "View Account",
        }),
        kpiCard({
          id: "meta_roas",
          metric: "Meta ROAS",
          value: "4.43x",
          change: "↑ 8.7%",
          status: "warning",
          statusText: "Declining",
          subtext: "Combined Meta ROAS is 4.43x vs 3.0x target",
          prev: "4.08x",
          bench: "3.0x",
          diags: [
            ["check", "Combined Meta ROAS at 4.43x vs 3.0x target."],
            ["check", "Celebrity Drapes ads performing above target."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "google_spend",
          metric: "Google Ad Spend",
          value: "£3,916",
          change: "↓ 15.3%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Your subscribed services are currently generating £3,916 vs £5,000 in Google Ads spend.",
          prev: "£4,624",
          bench: "£5,000",
          diags: [
            ["check", "Google Ads spend is pacing under the £5,000 daily target."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "meta_revenue",
          metric: "Meta Revenue",
          value: "£17,353",
          change: "↑ 8.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Meta Revenue is £17,353 vs £20,000 target",
          prev: "£15,990",
          bench: "£20,000",
          diags: [
            ["check", "Meta Revenue is generating 28% to £17,353, currently valued vs spend."],
          ],
          cta: "Fix",
        }),
        kpiCard({
          id: "meta_spend",
          metric: "Meta Ad Spend",
          value: "£6,286",
          change: "↑ 8.5%",
          status: "warning",
          statusText: "Declining",
          subtext: "Total spend is £6,286 vs £10,000 target",
          prev: "£5,793",
          bench: "£10,000",
          diags: [
            ["check", "Your Customer Lifecycle has slowed in 8 days vs last 30 days. Use lookalike based campaign creation."],
          ],
          cta: "Monitor",
        }),
        kpiCard({
          id: "blended_spend",
          metric: "Blended Ad Spend",
          value: "£20,502",
          change: "↑ 8.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Combined spend is £20,502 vs £25,000 target",
          prev: "£18,900",
          bench: "£25,000",
          diags: [
            ["check", "Combined ad spend declined 15% vs prior period. Mix is Meta-heavy."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "cpm_trend",
          metric: "Google Ad Spend",
          value: "£2,352",
          change: "↑ 8.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Spend is £2,352 for the period",
          prev: "£2,168",
          bench: "£3,000",
          diags: [
            ["check", "Your marketing campaigns are showing acceptable efficiency. Meta is outperforming Google."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "google_revenue",
          metric: "Google Revenue",
          value: "£3,149",
          change: "↓ 39.6%",
          status: "critical",
          statusText: "Critical",
          subtext: "Google Ads revenue dropped 39% to £3,149 for the period",
          prev: "£5,214",
          bench: "£8,000",
          diags: [
            ["wrong", "Google Ads revenue dropped 39% to £3,149, indicating weak ad performance vs budget."],
            ["wrong", "Pause under-target / match types immediately."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "orders_count",
          metric: "Total Orders",
          value: "25",
          change: "↑ 19.0%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "25 orders yesterday vs 21 in the day-ref period.",
          prev: "21",
          bench: "30",
          diags: [
            ["check", "Order volume is recovering with Meta as the primary driver."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "blended_revenue",
          metric: "Blended Revenue (Ads)",
          value: "£20,502",
          change: "↑ 8.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Attributed ad revenue is £20,502 vs £25,000 target.",
          prev: "£18,900",
          bench: "£25,000",
          diags: [
            ["check", "Attributed ad revenue is tracking with blended spend at 3.27x ROAS."],
          ],
          cta: "Review",
        }),
      ],
    },
    product: {
      title: "Product",
      cards: [
        kpiCard({
          id: "sku_performance",
          metric: "Top SKU Performance",
          value: "CD-1150",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Madhuri Inspired Pink Saree is the #1 SKU this period.",
          prev: "CD-1148",
          bench: "Top SKU",
          diags: [
            ["check", "CD-1150 generated £77,987 this period with 1.1% contribution."],
          ],
          cta: "Promote",
          extra: { aiData: { sku_label: "Madhuri Inspired Pink Saree" } },
        }),
        kpiCard({
          id: "underperforming_skus",
          metric: "Underperforming SKUs",
          value: "145",
          change: "↓ 13.7%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "145 SKUs are underperforming vs 168 last period.",
          prev: "168",
          bench: "<150 SKUs",
          diags: [
            ["check", "Underperforming SKU count dropped from 168 to 145."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "aov",
          metric: "Average Order Value",
          value: "£4,432",
          change: "↑ 0.4%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "AOV held at £4,432 vs £4,000-£5,000 target.",
          prev: "£4,414",
          bench: "£4,000",
          diags: [
            ["check", "AOV is stable. Bundles and drapes add-ons can lift ticket size."],
          ],
          cta: "Upsell",
        }),
        kpiCard({
          id: "return_rate",
          metric: "Refund & Return Rate",
          value: "4.0%",
          change: "↓ 1.1%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Returns are 4.0% of revenue vs <5% target.",
          prev: "5.1%",
          bench: "<5%",
          diags: [
            ["check", "Return rate improved. Size-guide PDPs on heavy sarees are helping."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "inventory_risk",
          metric: "Inventory Risk",
          value: "12 SKUs",
          change: "↑ 20%",
          status: "warning",
          statusText: "Watch",
          subtext: "12 hero SKUs are at stockout risk this week.",
          prev: "10 SKUs",
          bench: "<8 SKUs",
          diags: [
            ["wrong", "Hero drapes SKUs have fewer than 5 units remaining."],
          ],
          cta: "Reorder",
        }),
        kpiCard({
          id: "gross_margin",
          metric: "Gross Margin",
          value: "58%",
          change: "↑ 5.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Gross margin improved to 58% vs 52% industry avg.",
          prev: "55%",
          bench: "55-60%",
          diags: [
            ["check", "Supplier terms and lower discount depth lifted margin 3pts."],
          ],
          cta: "Review COGS",
        }),
        kpiCard({
          id: "sku_velocity_rank",
          metric: "SKU Velocity Rank Shift",
          value: "+6",
          change: "↑ 6",
          status: "healthy",
          statusText: "Healthy",
          subtext: "6 SKUs jumped into the top-20 velocity set.",
          prev: "0",
          bench: "+3",
          diags: [
            ["check", "Festive sarees are accelerating; winter shawls are slowing."],
          ],
          cta: "Analyze",
        }),
        kpiCard({
          id: "discount_depth",
          metric: "Discount Depth Analysis",
          value: "15%",
          change: "↓ 16.7%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Average discount depth reduced to 15%.",
          prev: "18%",
          bench: "10-20%",
          diags: [
            ["check", "Tighter promo calendar improved margin without killing conversion."],
          ],
          cta: "Optimize",
        }),
        kpiCard({
          id: "bundle_attach_rate",
          metric: "Bundle Attach Rate",
          value: "8.2%",
          change: "↑ 12%",
          status: "warning",
          statusText: "Watch",
          subtext: "Bundles attach on 8.2% of orders vs 12% target.",
          prev: "7.3%",
          bench: "12%",
          diags: [
            ["wrong", "Jewellery and blouse bundles are under-attached on saree PDPs."],
          ],
          cta: "Bundle",
        }),
        kpiCard({
          id: "contribution_margin",
          metric: "Contribution Margin / Order",
          value: "£1,850",
          change: "↑ 10.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Contribution margin per order is healthy at £1,850.",
          prev: "£1,674",
          bench: "£1,600",
          diags: [
            ["check", "Top 20% products contribute 65% of total margin."],
          ],
          cta: "Optimize",
        }),
        kpiCard({
          id: "rev_per_ad_sku",
          metric: "Revenue Per Ad £ by SKU",
          value: "£1,253",
          change: "↑ 6.2%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Revenue per ad pound is £1,253.",
          prev: "£1,180",
          bench: "£1,000+",
          diags: [
            ["check", "Top ad SKU generates £2,450 per £1,000 spent."],
          ],
          cta: "Scale",
        }),
        kpiCard({
          id: "sku_profitability",
          metric: "SKU Profitability Score",
          value: "78",
          change: "↑ 8.3%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "SKU profitability score improved to 78/100.",
          prev: "72",
          bench: "70-80",
          diags: [
            ["check", "45% of SKUs score above 80 after ads, COGS and returns."],
          ],
          cta: "Analyze",
        }),
      ],
    },
    customer: {
      title: "Customer",
      cards: [
        kpiCard({
          id: "repeat_rate",
          metric: "Repeat Purchase Rate",
          value: "30.7%",
          change: "↑ 8.1%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "30.7% of customers returned for a second purchase.",
          prev: "28.4%",
          bench: "28%",
          diags: [
            ["check", "Repeat rate is above target. WhatsApp win-backs are converting."],
          ],
          cta: "Retention",
        }),
        kpiCard({
          id: "ltv",
          metric: "Customer LTV (30-day)",
          value: "£6,820",
          change: "↑ 8.8%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "30-day LTV is £6,820 vs £6,000 target.",
          prev: "£6,270",
          bench: "£6,000",
          diags: [
            ["check", "LTV:CAC is healthy at current £830 CAC."],
          ],
          cta: "Upsell",
        }),
        kpiCard({
          id: "churn_risk_segment",
          metric: "Churn Risk Segment",
          value: "167",
          change: "↑ 7.1%",
          status: "warning",
          statusText: "Watch",
          subtext: "167 customers inactive 90+ days.",
          prev: "156",
          bench: "<120",
          diags: [
            ["wrong", "High-value drapes buyers are lapsing after one festive purchase."],
          ],
          cta: "Win-back",
        }),
        kpiCard({
          id: "new_returning_split",
          metric: "New vs Returning Split",
          value: "69% / 31%",
          change: "↑ 6.2%",
          status: "warning",
          statusText: "Watch",
          subtext: "69% new customers, 31% returning.",
          prev: "65% / 35%",
          bench: "60 / 40",
          diags: [
            ["wrong", "Acquisition is outrunning retention. Loyalty offers can rebalance mix."],
          ],
          cta: "Loyalty",
        }),
        kpiCard({
          id: "nps_score",
          metric: "NPS / Review Score",
          value: "72",
          change: "↑ 5.9%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "NPS 72 with 4.2/5 average rating.",
          prev: "68",
          bench: "70-80",
          diags: [
            ["check", "1,247 reviews collected this period. Delivery comments remain the gap."],
          ],
          cta: "Collect Reviews",
        }),
        kpiCard({
          id: "post_purchase_engagement",
          metric: "Post-Purchase Engagement",
          value: "38%",
          change: "↑ 9%",
          status: "warning",
          statusText: "Watch",
          subtext: "38% open WhatsApp / email post-purchase flows vs 45% target.",
          prev: "35%",
          bench: "45%",
          diags: [
            ["wrong", "Care-instruction WhatsApps underperform vs styling content."],
          ],
          cta: "Improve Flow",
        }),
        kpiCard({
          id: "first_to_repeat_gap",
          metric: "First-to-Repeat Gap",
          value: "45 days",
          change: "↓ 13.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Average days to second purchase improved to 45.",
          prev: "52 days",
          bench: "30-60 days",
          diags: [
            ["check", "Payback is inside 45 days at current CAC."],
          ],
          cta: "Optimize CAC",
        }),
        kpiCard({
          id: "subscription_penetration",
          metric: "Subscription Penetration",
          value: "2.1%",
          change: "↑ 0.4%",
          status: "warning",
          statusText: "Watch",
          subtext: "Recurring orders are 2.1% of sales.",
          prev: "1.7%",
          bench: "5%",
          diags: [
            ["wrong", "Styling-club subscription is still a small slice of revenue."],
          ],
          cta: "Launch Offer",
        }),
        kpiCard({
          id: "high_value_segment",
          metric: "High-Value Customer Segment",
          value: "£1.84M",
          change: "↑ 11%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Top 10% customers generated £1.84M this period.",
          prev: "£1.66M",
          bench: "£1.5M",
          diags: [
            ["check", "VIP drapes buyers are concentrating revenue. Protect them from churn."],
          ],
          cta: "VIP List",
        }),
        kpiCard({
          id: "payback_period",
          metric: "Payback Period / Customer",
          value: "45 days",
          change: "↓ 13.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Payback period reduced to 45 days.",
          prev: "52 days",
          bench: "30-60 days",
          diags: [
            ["check", "CAC £830 vs 30-day LTV £6,820 - payback is healthy."],
          ],
          cta: "Optimize CAC",
        }),
        kpiCard({
          id: "retention_revenue_pct",
          metric: "Retention Revenue %",
          value: "31%",
          change: "↑ 3.2%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "31% of revenue came from existing customers.",
          prev: "28%",
          bench: "35%",
          diags: [
            ["check", "Retention share is rising but still below the 35% target."],
          ],
          cta: "Retention",
        }),
        kpiCard({
          id: "cohort_decay_rate",
          metric: "Cohort Revenue Decay Rate",
          value: "42%",
          change: "↓ 4%",
          status: "warning",
          statusText: "Watch",
          subtext: "Month-2 cohort revenue drops 42% vs 35% target.",
          prev: "46%",
          bench: "35%",
          diags: [
            ["wrong", "Festive cohorts decay fast. Schedule a 30-day replenishment offer."],
          ],
          cta: "Fix Cohort",
        }),
      ],
    },
    store: {
      title: "Store",
      cards: [
        kpiCard({
          id: "conversion_rate",
          metric: "Conversion Rate",
          value: "7.3%",
          change: "↑ 10.0%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Store conversion rate is 7.3% vs 3.0% target.",
          prev: "6.6%",
          bench: "3.0%",
          diags: [
            ["check", "CVR is strong. Mobile is 6.8%, desktop 8.1%."],
          ],
          cta: "Optimize Checkout",
        }),
        kpiCard({
          id: "cart_abandonment",
          metric: "Cart Abandonment Rate",
          value: "68.5%",
          change: "↓ 2.4%",
          status: "warning",
          statusText: "Watch",
          subtext: "Cart abandonment is 68.5% vs <60% target.",
          prev: "70.2%",
          bench: "<60%",
          diags: [
            ["wrong", "Main drop is at shipping (45%) then payment (23%)."],
          ],
          cta: "Recover Carts",
        }),
        kpiCard({
          id: "site_speed",
          metric: "Site Speed - Mobile",
          value: "72",
          change: "↑ 4%",
          status: "warning",
          statusText: "Watch",
          subtext: "Mobile performance score 72 vs 80+ target.",
          prev: "69",
          bench: "80+",
          diags: [
            ["wrong", "PDP image weight is slowing mobile LCP on collection pages."],
          ],
          cta: "Speed Up",
        }),
        kpiCard({
          id: "pdp_ctr",
          metric: "Product Page CTR (ATC Rate)",
          value: "8.5%",
          change: "↑ 9.0%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Add-to-cart rate on PDPs is 8.5%.",
          prev: "7.8%",
          bench: "7-10%",
          diags: [
            ["check", "Top saree PDPs convert at 15% ATC."],
          ],
          cta: "Optimize PDP",
        }),
        kpiCard({
          id: "search_discovery",
          metric: "Search & Discovery",
          value: "42%",
          change: "↑ 10.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Internal search converts at 42%.",
          prev: "38%",
          bench: "35-45%",
          diags: [
            ["check", "Search converts at 42% vs browse at 2.8%. 15% of traffic uses search."],
          ],
          cta: "Improve Search",
        }),
        kpiCard({
          id: "checkout_dropoff",
          metric: "Checkout Drop-off Steps",
          value: "68.5%",
          change: "↓ 2.4%",
          status: "warning",
          statusText: "Watch",
          subtext: "Checkout drop-off is 68.5%.",
          prev: "70.2%",
          bench: "60-70%",
          diags: [
            ["wrong", "Shipping step is the largest leak. Guest checkout is underused."],
          ],
          cta: "Simplify Checkout",
        }),
        kpiCard({
          id: "mobile_desktop_gap",
          metric: "Mobile vs Desktop CVR Gap",
          value: "1.3 pts",
          change: "↓ 8%",
          status: "warning",
          statusText: "Watch",
          subtext: "Desktop CVR leads mobile by 1.3 points.",
          prev: "1.4 pts",
          bench: "<1.0 pts",
          diags: [
            ["wrong", "Mobile is 56% of traffic at 6.8% CVR vs desktop 8.1%."],
          ],
          cta: "Optimize Mobile",
        }),
        kpiCard({
          id: "collection_bounce",
          metric: "Collection Page Bounce",
          value: "45.2%",
          change: "↓ 6.8%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Collection bounce rate reduced to 45.2%.",
          prev: "48.5%",
          bench: "40-50%",
          diags: [
            ["check", "Top collections bounce at 35%. Imagery and sorting helped."],
          ],
          cta: "Improve UX",
        }),
        kpiCard({
          id: "image_quality_cvr",
          metric: "Image Quality vs Conversion",
          value: "72",
          change: "↑ 5.9%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Media quality score 72 correlates with CVR.",
          prev: "68",
          bench: "65-75",
          diags: [
            ["check", "High-quality pages (score >80) convert at 4.5% vs 2.1% for low-quality."],
          ],
          cta: "Improve Content",
        }),
        kpiCard({
          id: "rpv",
          metric: "Revenue Per Session",
          value: "£22",
          change: "↑ 6.4%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Revenue per session is £22.",
          prev: "£21",
          bench: "£20+",
          diags: [
            ["check", "RPS is healthy given 7.3% CVR and £4,432 AOV."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "ad_cost_conv_session",
          metric: "Ad Cost / Converted Session",
          value: "£830",
          change: "↓ 11.8%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Cost per converted session matches blended CAC at £830.",
          prev: "£940",
          bench: "£1,200",
          diags: [
            ["check", "Paid sessions that convert are cheaper than last period."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "traffic_quality_score",
          metric: "Traffic Quality Score",
          value: "78",
          change: "↑ 5.4%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Traffic quality score improved to 78/100.",
          prev: "74",
          bench: "70-80",
          diags: [
            ["check", "Organic traffic quality 85, paid 72."],
          ],
          cta: "Improve Targeting",
        }),
      ],
    },
    ai: {
      title: "AI Predictions",
      cards: [
        kpiCard({
          id: "revenue_forecast",
          metric: "Revenue Forecast",
          value: "£47,129",
          change: "↑ 10.9%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Daily revenue avg £5,983 based on last 7 days.",
          prev: "£42,500",
          bench: "£2,500++",
          diags: [
            ["check", "Daily revenue avg £5,983 based on last 7 days."],
            ["wrong", "Revenue down 50% vs peak - review top channel."],
          ],
          cta: "Review",
        }),
        kpiCard({
          id: "churn_probability",
          metric: "Churn Probability",
          value: "75%",
          change: "↑ 7.1%",
          status: "critical",
          statusText: "Critical",
          subtext: "75% of 2,137 customers have reordered.",
          prev: "70%",
          bench: "75%",
          diags: [
            ["wrong", "187 of 2,202 customers have reordered. 167 customers inactive 90+ days."],
          ],
          cta: "Win-back",
        }),
        kpiCard({
          id: "next_best_sku",
          metric: "Next Best SKU to Promote",
          value: "CD-1150",
          change: "↑ 0.2%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Madhuri Inspired Pink Saree is the next SKU to scale.",
          prev: "CD-1148",
          bench: "Top velocity",
          diags: [
            ["check", "Madhuri Inspired Pink Saree with 1.1% share. Top SKU generated £77,987 this period."],
          ],
          cta: "Feature SKU",
          extra: { aiData: { sku_label: "Madhuri Inspired Pink Saree" } },
        }),
        kpiCard({
          id: "predicted_stockout",
          metric: "Predicted Stockout",
          value: "333 SKUs",
          change: "↑ 16.8%",
          status: "critical",
          statusText: "Critical",
          subtext: "333 SKUs currently out of stock.",
          prev: "285 SKUs",
          bench: "0 SKUs",
          diags: [
            ["wrong", "145 SKUs currently out of stock. 187 SKUs have fewer than 5 units remaining."],
          ],
          cta: "Reorder Now",
        }),
        kpiCard({
          id: "optimal_send_time",
          metric: "Optimal Send Time (WhatsApp)",
          value: "7:30 PM IST",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Highest engagement window is 7:00-8:00 PM IST.",
          prev: "8:00 PM IST",
          bench: "Evening",
          diags: [
            ["check", "Festive drapes buyers engage after work. Avoid noon sends."],
          ],
          cta: "Schedule",
        }),
        kpiCard({
          id: "winback_revenue_potential",
          metric: "Win-back Revenue Potential",
          value: "£480K",
          change: "↑ 12%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "£480K recoverable from 167 lapsed high-value customers.",
          prev: "£430K",
          bench: "£300K+",
          diags: [
            ["check", "A 15% drapes voucher to 90-day lapsed VIPs is the highest-ROI play."],
          ],
          cta: "Launch Win-back",
        }),
        kpiCard({
          id: "demand_forecast",
          metric: "Demand Forecast",
          value: "-59%",
          change: "↓ 31.1%",
          status: "critical",
          statusText: "Critical",
          subtext: "Revenue fell 59% vs prior period.",
          prev: "-45%",
          bench: "-59%",
          diags: [
            ["wrong", "Revenue fell 59% vs prior period. Based on last 90 days of KPI history."],
          ],
          cta: "Hold",
        }),
        kpiCard({
          id: "price_elasticity",
          metric: "Price Elasticity Insight",
          value: "£54",
          change: "↑ 12.5%",
          status: "warning",
          statusText: "Watch",
          subtext: "Blended ROAS is 3.27x - demand appears neutral.",
          prev: "£48",
          bench: "£54 (Neutral)",
          diags: [
            ["wrong", "Be cautious with price increases given current ROAS and MER."],
          ],
          cta: "Test Pricing",
        }),
      ],
    },
    payment: {
      title: "Payment",
      cards: [
        kpiCard({
          id: "rzp_psr",
          metric: "Payment Success Rate",
          value: "95.3%",
          change: "↓ 0.8%",
          status: "warning",
          statusText: "Watch",
          subtext: "Payment success rate declined to 95.3%.",
          prev: "96.1%",
          bench: "96-98%",
          diags: [
            ["wrong", "4.7% failure rate needs attention, mainly UPI timeouts."],
          ],
          cta: "Investigate",
        }),
        kpiCard({
          id: "rzp_atv",
          metric: "Avg Transaction Value",
          value: "£4,432",
          change: "↑ 0.4%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Average successful payment is £4,432.",
          prev: "£4,414",
          bench: "£4,000-£5,000",
          diags: [
            ["check", "Mobile ATV £4,120, desktop £4,780."],
          ],
          cta: "Upsell",
        }),
        kpiCard({
          id: "rzp_total",
          metric: "Total Payment Attempts",
          value: "28",
          change: "↑ 10.5%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "28 payment attempts yesterday.",
          prev: "25",
          bench: "25+",
          diags: [
            ["check", "26 successful, 2 failed. Volume is in line with order count."],
          ],
          cta: "Monitor",
        }),
        kpiCard({
          id: "rzp_failure",
          metric: "Failure Rate by Reason",
          value: "4.7%",
          change: "↑ 20.5%",
          status: "warning",
          statusText: "Watch",
          subtext: "Failure rate increased to 4.7%.",
          prev: "3.9%",
          bench: "<4%",
          diags: [
            ["wrong", "Top reasons: Insufficient funds 45%, declined 35%, timeout 20%."],
          ],
          cta: "Retry Logic",
        }),
        kpiCard({
          id: "rzp_refund",
          metric: "Refund Rate",
          value: "2.8%",
          change: "↑ 12.0%",
          status: "warning",
          statusText: "Watch",
          subtext: "Refund rate increased to 2.8%.",
          prev: "2.5%",
          bench: "<2.5%",
          diags: [
            ["wrong", "Main reason: product quality / colour mismatch (60%)."],
          ],
          cta: "Quality Check",
        }),
        kpiCard({
          id: "rzp_methods",
          metric: "Payment Method Split",
          value: "UPI 72%",
          change: "↑ 5.9%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "UPI 72%, Card 20%, Wallet 8%.",
          prev: "UPI 68%",
          bench: "UPI 70%",
          diags: [
            ["check", "UPI remains the primary method. Cards convert a higher ATV."],
          ],
          cta: "Optimize Methods",
        }),
        kpiCard({
          id: "rzp_settlement",
          metric: "Settlement Delay Avg",
          value: "T+2.5",
          change: "↓ 10.7%",
          status: "healthy",
          statusText: "Healthy",
          subtext: "Settlement delay reduced to 2.5 days.",
          prev: "T+2.8",
          bench: "T+2-3",
          diags: [
            ["check", "T+2 for most UPI. Instant settlement on 15% of captures."],
          ],
          cta: "Monitor",
        }),
        kpiCard({
          id: "rzp_chargebacks",
          metric: "Chargeback Rate",
          value: "0.25%",
          change: "↑ 66.7%",
          status: "critical",
          statusText: "Critical",
          subtext: "Chargeback rate increased to 0.25%.",
          prev: "0.15%",
          bench: "<0.2%",
          diags: [
            ["wrong", "Chargebacks up 60% period-over-period. Enable 3DS on high-risk orders."],
          ],
          cta: "Enable 3DS",
        }),
      ],
    },
  };
}

function normalizeDemoConnectedServices(list: any): string[] {
  const mapped = (Array.isArray(list) ? list : []).map((s: string) =>
    s === "meta" ? "meta_ads" : s,
  );
  return Array.from(
    new Set([
      ...mapped,
      "shopify",
      "meta_ads",
      "google_ads",
      "ga4",
      "gsc",
      "razorpay",
      "klaviyo",
    ]),
  );
}

/**
 * Build a Scaleboard-ready CEO report. KPI sections are the full live-style
 * board (GBP). marketing_intel / organic_intel come from demo JSON.
 */
export function getDemoCeoReport(period: string = "yesterday"): any {
  const raw = getDemoFile("socket-ceo-report");
  const report = raw?.report ?? raw ?? null;
  if (!report) return null;

  const sections = buildDemoScaleboardSections();
  const label = PERIOD_LABELS[period] || "Yesterday";
  const additiveFactor = ADDITIVE_MULTIPLIER[period] ?? 1;
  const rateFactor = RATE_MULTIPLIER[period] ?? 1;

  Object.values(sections).forEach((section) => {
    section.cards.forEach((c: any) => {
      const factor = ADDITIVE_CARD_IDS.has(c.id) ? additiveFactor : rateFactor;
      if (c.data) {
        c.data.timeframe = label;
        if (typeof c.data.value === "string") c.data.value = scaleDemoValue(c.data.value, factor);
        if (typeof c.data.change === "string") c.data.change = scaleDemoValue(c.data.change, rateFactor);
      }
      if (Array.isArray(c.comparisons)) {
        c.comparisons = c.comparisons.map((comp: any) =>
          typeof comp.value === "string" ? { ...comp, value: scaleDemoValue(comp.value, factor) } : comp
        );
      }
    });
  });

  const visible = Object.values(sections).flatMap((s) => s.cards.map((c: any) => c.id));
  const order: Record<string, string[]> = {};
  Object.entries(sections).forEach(([key, section]) => {
    order[key] = section.cards.map((c: any) => c.id);
  });

  return {
    ...report,
    shop: getDemoMe()?.default_store ?? report.shop,
    periodLabel: label,
    compLabel: "Day Ref Period",
    connected_services: normalizeDemoConnectedServices(report.connected_services),
    card_preferences: { visible, order },
    summary: report.summary || { metricsCount: visible.length, criticalCount: 6, warningCount: 12 },
    sections,
  };
}

/**
 * Build a ReportsPage-shaped CEO report (raw `sections.<name>.cards` become
 * top-level `marketing/product/customer/store/ai` with cards whose ids match
 * ReportsPage's ALLOWED_CARD_IDS so they pass its `isCardAllowed` filter).
 * Uses GBP formatting to match the rest of the demo.
 */
export function getDemoReportsReport(period = "yesterday"): any {
  const card = (
    id: string,
    metric: string,
    value: number | string,
    format: string | undefined,
    changePct: number,
    status: string,
  ) => {
    const v = typeof value === "number" ? fmtDemoValue(value, format) : value;
    const change = fmtDemoChange(changePct);
    return {
      id,
      metric,
      status,
      statusText:
        status === "healthy"
          ? "Healthy"
          : status === "warning"
          ? "Watch"
          : status === "critical"
          ? "Critical"
          : "Info",
      data: {
        timeframe: "Yesterday",
        value: v,
        change,
        subtext: `${metric} is ${v}${change ? ` (${change} vs previous period)` : ""}.`,
      },
      comparisons: [
        { label: "Previous", value: v },
        { label: "Change", value: change || "0%" },
      ],
      diagnosis: [
        {
          type: status === "critical" || status === "warning" ? "cross" : "check",
          text:
            status === "critical" || status === "warning"
              ? `${metric} needs attention this period.`
              : `${metric} is trending in a healthy range.`,
        },
      ],
      primaryCTA: { label: "View details" },
    };
  };

  return {
    shop: getDemoMe()?.default_store ?? "urbancraft-demo.myshopify.com",
    period,
    marketing: {
      cards: [
        card("revenue", "Total Revenue", 2850000, "currency", 19.75, "healthy"),
        card("daily_ad_spend", "Total Ad Spend", 1000000, "currency", 13.64, "warning"),
        card("blended_roas", "Blended ROAS", "3.28x", undefined, 8.97, "healthy"),
        card("blended_cac", "Blended CAC", 980, "currency", -6.67, "healthy"),
        card("orders_count", "Total Orders", 4100, "number", 19.19, "healthy"),
        card("meta_roas", "Meta Ads Spend & ROAS", "3.42x", undefined, 8.57, "healthy"),
        card("google_roas", "Google Ads Spend & ROAS", "3.06x", undefined, 7.75, "healthy"),
        card("cpm_trend", "CPM Trend", "£182", undefined, -4.2, "healthy"),
      ],
    },
    product: {
      cards: [
        card("aov", "AOV", 695, "currency", 0.43, "info"),
        card("top_5_skus", "Top 5 SKUs by Revenue", "Premium Hoodie", undefined, 0, "healthy"),
        card("return_rate_sku", "Return Rate by SKU", "4.0%", undefined, -1.1, "healthy"),
      ],
    },
    customer: {
      cards: [
        card("repeat_purchase_rate", "Repeat Purchase Rate", "30.7%", undefined, 8.1, "healthy"),
        card("new_vs_returning_split", "New vs Returning Split", "69% / 31%", undefined, 0, "info"),
        card("returning_revenue_pct", "Returning Customer Revenue %", "31%", undefined, 3.2, "healthy"),
      ],
    },
    store: {
      cards: [
        card("total_sessions", "Total Sessions", 128000, "number", 19.07, "healthy"),
        card("conversion_rate", "Store Conversion Rate", "3.2%", undefined, 9.97, "healthy"),
        card("bounce_rate", "Bounce Rate", "38.2%", undefined, -4.74, "healthy"),
        card("cart_abandonment", "Cart Abandonment Rate", "68.5%", undefined, -2.1, "warning"),
        card("add_to_cart_rate", "Add-to-Cart Rate", "8.0%", undefined, 5.2, "healthy"),
        card("avg_session_duration", "Avg Session Duration", "186s", undefined, 7.51, "healthy"),
      ],
    },
    ai: {
      cards: [
        card("profit_margin", "Profit Margin (Blended)", "42.5%", undefined, 3.1, "healthy"),
        card("revenue_per_session", "Revenue per Session (Blended)", "£22", undefined, 6.4, "healthy"),
      ],
    },
  };
}

/** ExecutiveSummaryPage-shaped payload ({ success, report:{ report, summary, shopify, ... } }). */
export function getDemoExecutiveSummary(): any {
  return getDemoFile("executive-summary");
}

/** Static demo chat data for the in-app iSight AI page ({ fallback, prompts }). */
export function getDemoChatData(): any {
  return getDemoFile("isight-chat");
}

/** ForecastSection-shaped payload (health_score, cards grouped, action_plan, risks). */
export function getDemoForecast(): any {
  const c = (
    label: string,
    projection: number,
    projectionDisplay: string,
    changePct: number,
    yesterday: number,
    yesterdayDisplay: string,
    status = "healthy",
  ) => ({
    label,
    projection,
    projection_display: projectionDisplay,
    change_pct: changePct,
    projection_change_pct: changePct,
    projection_status: changePct >= 0 ? "up" : "down",
    yesterday,
    yesterday_display: yesterdayDisplay,
    status,
  });

  return {
    success: true,
    generatedAt: new Date().toISOString(),
    forecast: {
      projection_month: "October 2026",
      health_score: {
        score: 78,
        label: "Good",
        one_liner: "Strong revenue momentum with healthy retention; watch payment failures.",
      },
      cards: {
        shopify: {
          revenue: c("Projected Revenue", 3320000, "£3.3M", 16.49, 95000, "£95K"),
          orders: c("Projected Orders", 4780, "4,780", 16.59, 137, "137"),
          aov: c("Average Order Value", 720, "£720", 3.6, 695, "£695"),
        },
        marketing: {
          ad_spend: c("Projected Ad Spend", 1120000, "£1.1M", 12.0, 34000, "£34K", "warning"),
          roas: c("Projected ROAS", 3.4, "3.40x", 3.7, 3.28, "3.28x"),
        },
        traffic: {
          conversion: c("Projected Conversion", 3.4, "3.40%", 6.2, 3.2, "3.20%"),
        },
      },
      action_plan: [
        {
          title: "Scale winning Meta creatives",
          why: "Advantage+ Lookalike is returning 5.2x ROAS with headroom.",
          expected_impact: "+£180K revenue / month",
        },
        {
          title: "Cut underperforming Google Display",
          why: "Display Awareness Tier 2 is at 0.5x ROAS, burning spend.",
          expected_impact: "Save £28K / month",
        },
        {
          title: "Reduce payment failures",
          why: "Payment success rate slipped to 95.3%.",
          expected_impact: "Recover ~£40K / month",
        },
      ],
      risks: [
        {
          risk: "Rising chargebacks",
          why: "Chargebacks up 60% period-over-period.",
          fix: "Enable 3DS and review high-risk orders.",
        },
        {
          risk: "Content decay on top collection",
          why: "Summer Sale page dropped from pos 4.1 to 8.2.",
          fix: "Refresh on-page content and internal links.",
        },
      ],
    },
  };
}

const DEMO_KPI_FROM = "2026-07-25";
const DEMO_KPI_TO = "2026-08-23";
const DEMO_KPI_PREV_FROM = "2026-06-25";
const DEMO_KPI_PREV_TO = "2026-07-24";
const GBP_CURRENCY_SCALE = 0.039;

function hashString(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function buildDemoChart(base: number, seed: string) {
  const current: any[] = [];
  const previous: any[] = [];
  const jitter = (hashString(seed) % 40) / 100 + 0.8;
  for (let i = 0; i < 30; i++) {
    const wave = 0.72 + 0.28 * Math.sin((i / 30) * Math.PI * 2 + (hashString(seed) % 10));
    const currVal = Math.max(0, (base / 30) * wave * jitter * (0.92 + ((i * 13) % 17) / 80));
    const prevVal = Math.max(0, (base / 30) * wave * 0.86 * (0.9 + ((i * 7) % 11) / 60));
    current.push({ date: addDays(DEMO_KPI_FROM, i), period: "current", value: Math.round(currVal * 100) / 100 });
    previous.push({ date: addDays(DEMO_KPI_PREV_FROM, i), period: "previous", value: Math.round(prevVal * 100) / 100 });
  }
  return { current, previous };
}

function demoKpiSeed(key: string, format?: string): { current: number; previous: number } {
  const seeds: Record<string, { current: number; previous: number }> = {
    total_sales: { current: 110805, previous: 71555 },
    gross_sales: { current: 124600, previous: 81200 },
    net_sales: { current: 102440, previous: 66890 },
    daily_gmv: { current: 3693, previous: 2385 },
    total_gmv: { current: 118400, previous: 76400 },
    average_order_value: { current: 78, previous: 71 },
    taxes: { current: 18470, previous: 11920 },
    net_cash_flow_forecast: { current: 41200, previous: 28750 },
    total_orders: { current: 1420, previous: 1008 },
    new_customers: { current: 486, previous: 351 },
    returning_customers: { current: 612, previous: 544 },
    conversion_rate: { current: 3.42, previous: 2.91 },
    sales_growth: { current: 21.4, previous: 8.6 },
    units_per_transaction: { current: 1.8, previous: 1.6 },
    pending_dispatch: { current: 64, previous: 81 },
    order_fulfillment_rate: { current: 94.2, previous: 91.1 },
    referral_customers: { current: 118, previous: 92 },
    total_products_available: { current: 428, previous: 401 },
    meta_ads_spend: { current: 18420, previous: 15110 },
    meta_ads_revenue: { current: 61280, previous: 44890 },
    meta_ads_roas: { current: 3.33, previous: 2.97 },
    meta_ads_cac: { current: 38.2, previous: 43.1 },
    meta_ads_mer: { current: 3.33, previous: 2.97 },
    meta_ads_cpp: { current: 14.8, previous: 16.2 },
    meta_ads_ctr: { current: 1.84, previous: 1.61 },
    meta_ads_frequency: { current: 1.7, previous: 1.6 },
    meta_ads_scroll_stop_rate: { current: 22.4, previous: 19.8 },
    meta_ads_new_customers: { current: 214, previous: 176 },
    ga4_sessions: { current: 28410, previous: 24120 },
    ga4_users: { current: 19640, previous: 16880 },
    ga4_new_users: { current: 11220, previous: 9740 },
    ga4_organic_traffic: { current: 8640, previous: 7210 },
    ga4_bounce_rate: { current: 38.4, previous: 41.2 },
    ga4_avg_session_duration: { current: 164, previous: 151 },
    ga4_pages_per_session: { current: 3.4, previous: 3.1 },
    ga4_conversions: { current: 972, previous: 701 },
    ga4_conversion_rate: { current: 3.42, previous: 2.91 },
    ga4_revenue: { current: 75840, previous: 49770 },
    google_ads_spend: { current: 12680, previous: 10940 },
    google_ads_revenue: { current: 16990, previous: 17500 },
    google_ads_roas: { current: 1.34, previous: 1.6 },
    google_ads_cac: { current: 52.4, previous: 47.8 },
    google_ads_mer: { current: 1.34, previous: 1.6 },
    google_ads_cpp: { current: 18.6, previous: 16.9 },
    google_ads_new_customers: { current: 96, previous: 88 },
    impressions: { current: 412000, previous: 368400 },
    clicks: { current: 18420, previous: 16110 },
    ctr: { current: 4.47, previous: 4.37 },
    position: { current: 8.2, previous: 9.1 },
    total_spend: { current: 33840, previous: 28640 },
    roas: { current: 3.27, previous: 3.02 },
    mer: { current: 31.64, previous: 26.17 },
    blended_cac: { current: 41.6, previous: 47.2 },
    blended_cvr: { current: 3.18, previous: 2.84 },
    blended_new_customers: { current: 486, previous: 351 },
    cpc: { current: 0.92, previous: 0.84 },
    ltv_cac_ratio: { current: 4.8, previous: 4.1 },
    bfli_multiplicative: { current: 1.62, previous: 1.41 },
    total_payments: { current: 110805, previous: 71555 },
    captured_payments: { current: 107480, previous: 69020 },
    failed_payments: { current: 2140, previous: 1680 },
    refunded_payments: { current: 3185, previous: 1855 },
    chargebacks: { current: 277, previous: 166 },
    on_time_delivery_rate: { current: 91.4, previous: 88.6 },
    rto_rate: { current: 4.8, previous: 5.6 },
    wismo_rate: { current: 6.2, previous: 7.1 },
    avg_shipping_cost_order: { current: 4.85, previous: 5.1 },
    same_day_dispatch_rate: { current: 38.4, previous: 31.2 },
    ship_return_rate: { current: 7.6, previous: 6.9 },
    ndr_rate: { current: 3.1, previous: 3.8 },
    pick_pack_accuracy: { current: 98.4, previous: 97.6 },
    post_delivery_nps: { current: 54, previous: 48 },
    checkout_dropoff_rate: { current: 62.4, previous: 58.1 },
    checkout_prepaid_conversion_rate: { current: 71.2, previous: 66.8 },
    checkout_prepaid_cod_ratio: { current: 2.48, previous: 2.01 },
    total_reviews: { current: 312, previous: 248 },
    average_rating: { current: 4.6, previous: 4.4 },
    positive_review_rate: { current: 86.4, previous: 82.1 },
    negative_review_rate: { current: 6.8, previous: 8.4 },
    verified_purchase_rate: { current: 78.2, previous: 74.6 },
    photo_review_rate: { current: 24.1, previous: 19.8 },
    text_review_rate: { current: 91.4, previous: 88.2 },
    review_velocity: { current: 10.4, previous: 8.3 },
    published_review_rate: { current: 94.6, previous: 92.1 },
    top_5_reviewed_products: { current: 5, previous: 5 },
    top_5_reviewers: { current: 5, previous: 5 },
  };
  if (seeds[key]) return seeds[key];
  const h = hashString(key);
  const isPct = format === "percentage";
  const isDec = format === "decimal" || format === "score";
  const current = isPct ? 10 + (h % 40) : isDec ? 1 + (h % 400) / 100 : 80 + (h % 900);
  return { current, previous: Math.round(current * 0.82 * 100) / 100 };
}

function scaleCurrencyNumber(n: number): number {
  if (!Number.isFinite(n)) return n;
  if (Math.abs(n) < 250) return Math.round(n * 100) / 100;
  return Math.round(n * GBP_CURRENCY_SCALE * 100) / 100;
}

function scaleChartValues(chart: any, format?: string): any {
  if (format !== "currency" || !chart) return chart;
  const mapPts = (pts: any[]) =>
    (pts || []).map((p) => ({ ...p, value: scaleCurrencyNumber(Number(p?.value ?? 0)) }));
  return {
    ...chart,
    current: mapPts(chart.current || []),
    previous: mapPts(chart.previous || []),
    combined: chart.combined ? mapPts(chart.combined) : undefined,
  };
}

function makeUniversalWidgetEntry(key: string, format?: string, extra: Record<string, any> = {}) {
  const seed = demoKpiSeed(key, format);
  const chart = buildDemoChart(seed.current, key);
  const change_pct = seed.previous ? Number((((seed.current - seed.previous) / seed.previous) * 100).toFixed(2)) : 0;
  return {
    message: "Success",
    success: true,
    data: {
      current: seed.current,
      previous: seed.previous,
      metric: key,
      change_pct,
      chart,
      period: {
        current: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
        previous: { from: DEMO_KPI_PREV_FROM, to: DEMO_KPI_PREV_TO },
      },
      ...extra,
    },
  };
}

function reviewLeaderboardItems(kind: "products" | "reviewers") {
  if (kind === "reviewers") {
    return [
      { rank: 1, reviewer_name: "Priya Sharma", review_count: 14, average_rating: 4.9 },
      { rank: 2, reviewer_name: "Amelia Hughes", review_count: 11, average_rating: 4.8 },
      { rank: 3, reviewer_name: "Neha Kapoor", review_count: 9, average_rating: 4.7 },
      { rank: 4, reviewer_name: "Sophie Patel", review_count: 8, average_rating: 4.6 },
      { rank: 5, reviewer_name: "Ananya Rao", review_count: 7, average_rating: 4.5 },
    ];
  }
  return [
    { rank: 1, product_title: "Alia Inspired Georgette Saree", review_count: 48, average_rating: 4.8 },
    { rank: 2, product_title: "Bollywood Golden Net Saree", review_count: 41, average_rating: 4.7 },
    { rank: 3, product_title: "Banarasi Silk Wedding Saree", review_count: 36, average_rating: 4.9 },
    { rank: 4, product_title: "Pastel Organza Saree", review_count: 29, average_rating: 4.6 },
    { rank: 5, product_title: "Sequin Party Lehenga", review_count: 24, average_rating: 4.5 },
  ];
}

function getDemoUniversalKpi(): any {
  const base = JSON.parse(JSON.stringify(getDemoFile("kpi-universal") || { success: true, widgets: {} }));
  const formatByKey: Record<string, string> = {};
  flattenWidgetDefs().forEach((w) => {
    if (w?.key) formatByKey[w.key] = w.format || "number";
  });

  const widgets = base.widgets || {};
  Object.keys(widgets).forEach((key) => {
    const format = formatByKey[key];
    if (format !== "currency") return;
    const entry = widgets[key];
    const data = entry?.data;
    if (!data) return;
    if (typeof data.current === "number") data.current = scaleCurrencyNumber(data.current);
    if (typeof data.previous === "number") data.previous = scaleCurrencyNumber(data.previous);
    data.chart = scaleChartValues(data.chart, format);
  });

  flattenWidgetDefs().forEach((def) => {
    const key = def.key;
    if (!key || widgets[key]) return;
    const extra: Record<string, any> = {};
    if (key === "top_5_reviewed_products") {
      extra.items = reviewLeaderboardItems("products");
      extra.display = "list";
    }
    if (key === "top_5_reviewers") {
      extra.items = reviewLeaderboardItems("reviewers");
      extra.display = "list";
    }
    widgets[key] = makeUniversalWidgetEntry(key, def.format, extra);
  });

  base.widgets = widgets;
  base.data_coverage = {
    ...(base.data_coverage || {}),
    ga4: { token_expired: false, from: DEMO_KPI_FROM, to: DEMO_KPI_TO, connected: true },
    gsc: { token_expired: false, from: DEMO_KPI_FROM, to: DEMO_KPI_TO, connected: true },
    payments: { token_expired: false, from: DEMO_KPI_FROM, to: DEMO_KPI_TO, connected: true },
    meta: { token_expired: false, from: DEMO_KPI_FROM, to: DEMO_KPI_TO, connected: true },
    google_ads: { token_expired: false, from: DEMO_KPI_FROM, to: DEMO_KPI_TO, connected: true },
  };
  return base;
}

function getDemoDedicatedKpi(path: string): any {
  if (path === "/kpi/top-loss-items") {
    const items = [
      { title: "Alia Inspired Pink Saree", sku: "AIS-PNK-01", units: 18, loss: 1260 },
      { title: "Gold Embroidery Lehenga", sku: "GEL-GLD-04", units: 11, loss: 980 },
      { title: "Net Dupatta Set", sku: "NDS-IV-09", units: 24, loss: 740 },
      { title: "Banarasi Silk Blouse", sku: "BSB-MR-12", units: 9, loss: 610 },
      { title: "Sequin Cocktail Saree", sku: "SCS-BK-03", units: 7, loss: 455 },
      { title: "Velvet Bridal Lehenga", sku: "VBL-RD-07", units: 5, loss: 390 },
      { title: "Kalamkari Cotton Saree", sku: "KCS-BL-18", units: 16, loss: 320 },
      { title: "Chiffon Party Dupatta", sku: "CPD-GD-22", units: 21, loss: 245 },
    ];
    return {
      items,
      total_loss: items.reduce((sum, item) => sum + item.loss, 0),
    };
  }
  if (path === "/kpi/low-stock-items") {
    const items = [
      { title: "Alia Inspired Georgette Saree", quantity: 4 },
      { title: "Bollywood Golden Net Saree", quantity: 6 },
      { title: "Banarasi Silk Wedding Saree", quantity: 3 },
      { title: "Pastel Organza Saree", quantity: 8 },
      { title: "Sequin Party Lehenga", quantity: 2 },
      { title: "Velvet Bridal Lehenga", quantity: 5 },
      { title: "Kalamkari Cotton Saree", quantity: 7 },
      { title: "Net Dupatta Set", quantity: 9 },
    ];
    return { items, total_low_stock: items.length, threshold: 10 };
  }
  if (path === "/kpi/high-volume-products") {
    return {
      success: true,
      products: [
        { title: "Alia Inspired Georgette Saree", avg_30d_orders: 42, in_stock: 156, unit_price: 79.99 },
        { title: "Bollywood Golden Net Saree", avg_30d_orders: 38, in_stock: 89, unit_price: 119.0 },
        { title: "Banarasi Silk Wedding Saree", avg_30d_orders: 35, in_stock: 64, unit_price: 189.0 },
        { title: "Pastel Organza Saree", avg_30d_orders: 31, in_stock: 204, unit_price: 69.0 },
        { title: "Sequin Party Lehenga", avg_30d_orders: 27, in_stock: 48, unit_price: 249.0 },
        { title: "Alia Inspired Pink Saree", avg_30d_orders: 24, in_stock: 112, unit_price: 84.0 },
        { title: "Gold Embroidery Lehenga", avg_30d_orders: 21, in_stock: 37, unit_price: 219.0 },
        { title: "Net Dupatta Set", avg_30d_orders: 19, in_stock: 178, unit_price: 39.0 },
      ],
    };
  }
  if (path === "/kpi/out-of-stock-products") {
    return {
      success: true,
      total: 14,
      products: [
        { title: "Handloom Silk Saree", sku: "HSS-IV-14", last_in_stock: "2026-08-12" },
        { title: "Mirror Work Lehenga", sku: "MWL-PK-08", last_in_stock: "2026-08-16" },
        { title: "Zari Border Dupatta", sku: "ZBD-GD-21", last_in_stock: "2026-08-19" },
        { title: "Embroidered Anarkali", sku: "EMA-NV-03", last_in_stock: "2026-08-21" },
      ],
    };
  }
  if (path === "/kpi/top-bestsellers") {
    return {
      products: [
        { rank: 1, name: "Alia Inspired Georgette Saree", quantity_sold: 186, revenue: "14880", orders: 162, avg_order_value: "91.85" },
        { rank: 2, name: "Bollywood Golden Net Saree", quantity_sold: 141, revenue: "16780", orders: 128, avg_order_value: "131.09" },
        { rank: 3, name: "Banarasi Silk Wedding Saree", quantity_sold: 96, revenue: "18144", orders: 88, avg_order_value: "206.18" },
        { rank: 4, name: "Pastel Organza Saree", quantity_sold: 122, revenue: "8418", orders: 110, avg_order_value: "76.53" },
        { rank: 5, name: "Sequin Party Lehenga", quantity_sold: 74, revenue: "18426", orders: 68, avg_order_value: "271.00" },
        { rank: 6, name: "Alia Inspired Pink Saree", quantity_sold: 88, revenue: "7392", orders: 79, avg_order_value: "93.57" },
        { rank: 7, name: "Gold Embroidery Lehenga", quantity_sold: 51, revenue: "11169", orders: 47, avg_order_value: "237.64" },
        { rank: 8, name: "Net Dupatta Set", quantity_sold: 134, revenue: "5226", orders: 121, avg_order_value: "43.19" },
      ],
      total_products: 8,
      period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
    };
  }
  if (path === "/kpi/sales-by-location") {
    return {
      total_sales: "110805",
      locations: [
        { location: "England", sales: 64260, percentage: 58 },
        { location: "Scotland", sales: 22160, percentage: 20 },
        { location: "Wales", sales: 14390, percentage: 13 },
        { location: "Northern Ireland", sales: 9995, percentage: 9 },
      ],
      chart_data: [
        { name: "England", sales: 64260 },
        { name: "Scotland", sales: 22160 },
        { name: "Wales", sales: 14390 },
        { name: "N. Ireland", sales: 9995 },
      ],
      period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
    };
  }
  if (path === "/kpi/marketing-channels") {
    return {
      total_orders: 1420,
      total_revenue: "110805",
      channels: [
        { channel: "Paid Social", orders: 486, revenue: 41200, percentage: 37 },
        { channel: "Paid Search", orders: 312, revenue: 28940, percentage: 26 },
        { channel: "Organic", orders: 274, revenue: 22160, percentage: 20 },
        { channel: "Direct", orders: 198, revenue: 12480, percentage: 11 },
        { channel: "Email", orders: 150, revenue: 6025, percentage: 6 },
      ],
      chart_data: [
        { name: "Paid Social", value: 37, orders: 486, revenue: 41200 },
        { name: "Paid Search", value: 26, orders: 312, revenue: 28940 },
        { name: "Organic", value: 20, orders: 274, revenue: 22160 },
        { name: "Direct", value: 11, orders: 198, revenue: 12480 },
        { name: "Email", value: 6, orders: 150, revenue: 6025 },
      ],
      comparison: { value: 41200, text: "Paid Social leads revenue", is_positive: true, percentage: 12.4 },
    };
  }
  if (path === "/kpi/discount-offers") {
    return {
      total_discount_amount: "8420",
      total_orders_with_discount: 318,
      top_discount_codes: [
        { code: "WEDDING15", amount: 15, type: "percentage", usage_count: 126, total_savings: 3180 },
        { code: "FESTIVE10", amount: 10, type: "percentage", usage_count: 98, total_savings: 2460 },
        { code: "WELCOME20", amount: 20, type: "fixed_amount", usage_count: 64, total_savings: 1280 },
        { code: "VIP25", amount: 25, type: "percentage", usage_count: 30, total_savings: 1500 },
      ],
      chart_data: [
        { code: "WEDDING15", usage_count: 126, amount: 15, total_savings: 3180 },
        { code: "FESTIVE10", usage_count: 98, amount: 10, total_savings: 2460 },
        { code: "WELCOME20", usage_count: 64, amount: 20, total_savings: 1280 },
        { code: "VIP25", usage_count: 30, amount: 25, total_savings: 1500 },
      ],
      comparison: { value: 8420, text: "Discount spend vs last period", is_positive: false, percentage: 8.2 },
    };
  }
  if (path === "/kpi/order-status") {
    return {
      total_orders: 1420,
      financial_status: {
        breakdown: [
          { status: "Paid", count: 1184, percentage: 83.4 },
          { status: "Pending", count: 142, percentage: 10 },
          { status: "Refunded", count: 62, percentage: 4.4 },
          { status: "Partially refunded", count: 32, percentage: 2.2 },
        ],
        summary: { Paid: 1184, Pending: 142, Refunded: 62, "Partially refunded": 32 },
      },
      fulfillment_status: {
        breakdown: [
          { status: "Fulfilled", count: 1098, percentage: 77.3 },
          { status: "Unfulfilled", count: 214, percentage: 15.1 },
          { status: "Partial", count: 86, percentage: 6.1 },
          { status: "Restocked", count: 22, percentage: 1.5 },
        ],
        summary: { Fulfilled: 1098, Unfulfilled: 214, Partial: 86, Restocked: 22 },
      },
      period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
    };
  }
  if (path === "/kpi/order-shipping-status") {
    return {
      total_orders: 1420,
      pending: { count: 214, percentage: 15.1 },
      dispatched: { count: 386, percentage: 27.2 },
      delivered: { count: 820, percentage: 57.7 },
      chart_data: [
        { name: "Pending", value: 214, percentage: 15.1 },
        { name: "Dispatched", value: 386, percentage: 27.2 },
        { name: "Delivered", value: 820, percentage: 57.7 },
      ],
      period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
      source: "demo",
    };
  }
  if (path === "/kpi/total-products-available") {
    return {
      total_products: 428,
      total_quantity: 18640,
      in_stock_percentage: 86,
      breakdown: [
        { label: "Warehouse", percentage: 86 },
        { label: "Reserved", percentage: 9 },
        { label: "Incoming", percentage: 5 },
      ],
    };
  }
  if (path === "/checkout-kpi/leakage-funnel") {
    return {
      success: true,
      data: {
        funnel_steps: [
          { label: "Visited Checkout", count: 4200, percentage: 100, dropOff: 0, isBiggestLeak: false },
          { label: "Entered Address", count: 2940, percentage: 70, dropOff: 30, isBiggestLeak: true },
          { label: "Reached Payment", count: 2180, percentage: 51.9, dropOff: 25.9, isBiggestLeak: false },
          { label: "Payment Attempted", count: 1760, percentage: 41.9, dropOff: 19.3, isBiggestLeak: false },
          { label: "Order Confirmed", count: 1420, percentage: 33.8, dropOff: 19.3, isBiggestLeak: false },
        ],
        biggest_leakage: {
          step: "address_reached",
          drop_off_percentage: 30,
          label: "Entered Address",
          description: "30% of checkout visitors drop before completing address.",
        },
        aggregated: {
          checkout_started: 4200,
          address_reached: 2940,
          payment_reached: 2180,
          payment_attempts: 1760,
          checkout_completed: 1420,
          sessions_lost_per_day: 93,
          revenue_lost_per_day: 7250,
        },
        deltas: {
          drop_off_checkout_to_address_delta: 4.2,
          drop_off_address_to_payment_delta: -1.1,
          drop_off_payment_to_order_delta: 0.8,
        },
        period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
      },
    };
  }
  if (path === "/checkout-kpi/friction-index") {
    return {
      success: true,
      data: {
        total_abandoned_carts: 2780,
        friction_coupon_issue: 420,
        friction_shipping_shock: 860,
        friction_otp_failure: 190,
        friction_address_error: 510,
        friction_unknown: 800,
        friction_score: 6.4,
        friction_score_label: "Moderate",
        top_issue: { key: "friction_shipping_shock", label: "Shipping shock", percentage: 30.9 },
        support_tickets: 38,
        avg_friction_score: 5.8,
        period: { from: DEMO_KPI_FROM, to: DEMO_KPI_TO },
        data_source_flags: { otp_failure: false },
      },
    };
  }
  return undefined;
}

let demoFetchPatched = false;
function installDemoFetchPatch(): void {
  if (typeof window === "undefined" || demoFetchPatched) return;
  demoFetchPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isDemoMode()) return originalFetch(input as any, init);
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : (input as Request).url;
    const method =
      init?.method ||
      (typeof input !== "string" && !(input instanceof URL) ? (input as Request).method : "GET");
    const payload = resolveDemoResponse(method, url);
    if (payload !== DEMO_NO_MATCH) {
      return new Response(JSON.stringify(payload), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      });
    }
    return originalFetch(input as any, init);
  };
}

if (typeof window !== "undefined" && isDemoMode()) {
  installDemoFetchPatch();
}

/** Strip query string and normalize the request path for matching. */
function normalizePath(url: string): string {
  let path = url || "";
  // Absolute isight.netsights.ai URLs -> keep only the path after the origin.
  const absMatch = path.match(/^https?:\/\/[^/]+(\/.*)$/i);
  if (absMatch) path = absMatch[1];
  // Drop the leading /api if present (axios baseURL is /api, but absolute
  // isight URLs include /api explicitly).
  path = path.replace(/^\/api(?=\/)/, "");
  // Drop query string / hash.
  path = path.split("?")[0].split("#")[0];
  // Drop trailing slash (except root).
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path;
}

/**
 * Map a request (method + normalized path) to a demo-data JSON payload.
 * Returns `undefined` when there is no mapping (caller then lets the request
 * proceed to network as a graceful fallback).
 */
function parseQueryParams(url: string, extra?: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = { ...(extra || {}) };
  const query = (url || "").split("?")[1]?.split("#")[0];
  if (!query) return out;
  new URLSearchParams(query).forEach((value, key) => {
    if (out[key] == null) out[key] = value;
  });
  return out;
}

function mapRequestToDemo(method: string, path: string, body?: any, query: Record<string, any> = {}): any {
  const m = method.toUpperCase();
  const b = parseBody(body);

  // ── Auth ────────────────────────────────────────────────────────────────
  if (path === "/me") return getDemoMe();
  if (path === "/login" && m === "POST") return getDemoMe();
  if (path === "/logout") return { success: true };

  // ── User profile (Personal Information edit) ───────────────────────────
  if (path === "/user/profile" && m === "PUT") {
    const current = getDemoMe() || {};
    const updated = {
      ...current,
      ...(b.name != null ? { name: b.name } : {}),
      ...(b.email != null ? { email: b.email } : {}),
      ...(b.phone_number != null ? { phone_number: b.phone_number } : {}),
    };
    try { localStorage.setItem(DEMO_PROFILE_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
    return { success: true, user: updated };
  }
  // OTP flow: demo mode always "succeeds" and accepts any 6-digit code.
  if (path === "/user/send-otp" && m === "POST") return { success: true, expiresIn: 300 };
  if (path === "/user/verify-otp" && m === "POST") {
    const current = getDemoMe() || {};
    const field = b.type === "phone" ? "phone_verified_at" : "email_verified_at";
    const updated = { ...current, [field]: new Date().toISOString() };
    try { localStorage.setItem(DEMO_PROFILE_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
    return { success: true, user: updated };
  }

  // ── Billing / subscription (keep account usable) ──────────────────────────
  if (path === "/billing/choose-plan") {
    const data = getDemoFile("billing-choose-plan");
    const activatedKey = getDemoSelectedPlanKey();
    if (data && activatedKey && data.plans?.[activatedKey]) {
      const plan = data.plans[activatedKey];
      return {
        ...data,
        current_subscription: {
          ...(data.current_subscription || {}),
          plan_key: activatedKey,
          status: "active",
          price: plan.monthly_price ?? plan.price ?? 0,
          billing_cycle: data.current_subscription?.billing_cycle || "monthly",
          can_downgrade_to_free: true,
        },
      };
    }
    return data;
  }
  // Compare Plans page (feature matrix).
  if (path === "/features/compare-plans") {
    const data = getDemoFile("features-compare-plans");
    const activatedKey = getDemoSelectedPlanKey();
    if (data && activatedKey) return { ...data, current_plan_key: activatedKey };
    return data;
  }
  // Invoices page (credit orders + shopify subscription invoices).
  if (path === "/invoices") return getDemoFile("invoices");
  // Credits + gateway (BillingPage). Demo: static balance, no real payment.
  if (path === "/merchant-credits") {
    const c = getDemoFile("merchant-credits")?.credits ?? {};
    const total = c.total_credits ?? 500;
    const used = c.used_credits ?? 127;
    return {
      total_credits: total,
      used_credits: used,
      available_credits: c.remaining_credits ?? (total - used),
    };
  }
  if (path === "/detect-gateway") return getDemoFile("detect-gateway");
  if (path === "/credits/packs") {
    return {
      amount_min: 1,
      amount_max: 100,
      amount_step: 1,
      amounts: Array.from({ length: 100 }, (_, i) => ({ amount_usd: i + 1, credits: (i + 1) * 50 })),
    };
  }
  // Demo checkout endpoints are inert (no real gateway calls). "Try Now" /
  // "Activate" just persists the chosen plan locally and reports success -
  // the UI then flips straight to "Active" with no redirect and no error.
  if ((path === "/billing/start" || path === "/billing/woocommerce/start") && m === "POST") {
    if (b?.plan_key) setDemoSelectedPlanKey(String(b.plan_key));
    return { success: true, demo: true, activated_plan_key: b?.plan_key ?? null };
  }
  if (path === "/credits/create-order" && m === "POST") return { success: true, demo: true };
  if (path === "/credits/verify" && m === "POST") return { success: true, credits_added: 0 };
  // UnifiedGuard gates dashboard access on this - demo is always active.
  if (path === "/subscription/status") return { status: "active" };
  // Plan & Feature Access map (FeatureAccessContext) - unlocks Reports, etc.
  if (path === "/features") return getDemoFile("features");
  if (path === "/invitations/pending") return { invitations: [] };
  if (path === "/invitations/user/pending") return { invitations: [] };

  // ActiveStoreContext: array of stores (with permissions) drives sidebar +
  // dropdown visibility and permission checks.
  if (path === "/stores" || path === "/api/stores")
    return getDemoFile("stores");
  // useEnhancedPermissions fallback fetch (activeStore already carries perms).
  if (/^\/permissions\/stores\/[^/]+$/.test(path))
    return { success: true, permissions: JSON.stringify(getDemoFile("stores")?.[0]?.permissions ?? {}) };
  // Sidebar Payment tab + razorpay availability.
  if (path === "/razorpay/integration-status") return { is_active: true };
  if (path === "/user/advanced-settings")
    return { success: true, settings: { sight_enabled: 1, ai_intelligence: 1 } };
  // iSight sidebar entry gates on this; keep sights enabled for the demo.
  if (path === "/user/interface-settings") {
    const s = getDemoFile("interface-settings")?.settings ?? {};
    return { settings: { ...s, sights_enabled: true } };
  }
  // NOTE: /scalekit/sso-token is intentionally NOT mocked in demo mode. The
  // iSight and Scalekit sidebar buttons must use real, live SSO auth (they open
  // external apps that validate the token), so this call is allowed through to
  // the live backend instead of returning a fake demo token.

  // ── Scaleboard side calls (absolute isight URLs, path-normalized here) ────
  if (path === "/netsight/trial-status") return getDemoFile("trial-status");
  if (path === "/razorpay/metrics") return getDemoFile("razorpay-metrics");
  if (path === "/netsight/infrastructure-intelligence")
    return getDemoFile("infrastructure-intelligence");

  // ── Sight dashboard widgets ───────────────────────────────────────────────
  if (path === "/kpi/netsight/universal") return getDemoUniversalKpi();
  if (path.startsWith("/kpi/") || path.startsWith("/checkout-kpi/")) {
    const dedicated = getDemoDedicatedKpi(path);
    if (dedicated !== undefined) return dedicated;
  }
  if (path === "/sights") {
    if (m === "GET") return getDemoSightsList();
    if (m === "POST") {
      const name = String(b?.name || "New Sight");
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "sight";
      return {
        sight: {
          id: Date.now(),
          name,
          slug,
          md5_hash: `demo${Date.now().toString(16)}`,
          icon: b?.icon || "grid",
          description: b?.description || "",
        },
      };
    }
  }
  // Per-sight detail (any identifier, incl. "default") -> persisted selection
  // if the user has saved cards, else the static netboard sample.
  if (/^\/sights\/[^/]+$/.test(path) && m === "GET") {
    const id = path.split("/").pop() || "default";
    return buildDemoSight(id);
  }
  // Manage Cards save: persist the selection, then return the rebuilt sight so
  // the dashboard shows exactly the cards the user picked.
  const sightWidgetsMatch = path.match(/^\/sights\/([^/]+)\/widgets$/);
  if (sightWidgetsMatch) {
    const id = sightWidgetsMatch[1];
    if (m === "POST" || m === "PUT") saveDemoSightWidgets(id, body);
    return { success: true, ...buildDemoSight(id) };
  }
  // Reset -> clear the saved selection for this sight (back to the sample).
  const sightResetMatch = path.match(/^\/sights\/([^/]+)\/reset$/);
  if (sightResetMatch) {
    const id = sightResetMatch[1];
    const map = readSightWidgetMap();
    delete map[String(id)];
    delete map["default"];
    writeSightWidgetMap(map);
    return { success: true, ...buildDemoSight(id) };
  }
  if (path === "/update-widget-position") return { success: true };

  // ── Widget catalog / manager (SightWidgetManager "Add / Manage cards") ────
  if (path === "/widgets/available") return getDemoFile("widgets-available");
  if (path === "/widgets/merchant") return getDemoFile("widgets-merchant");
  if (path === "/widgets/usage") return getDemoFile("widgets-usage");
  if (path === "/widgets/data") {
    const key = String(query.widget_key || query.widgetKey || "");
    if (key === "top_5_reviewed_products" || key === "review_top_products" || key === "top_product_review") {
      return { success: true, data: { items: reviewLeaderboardItems("products"), display: "list" } };
    }
    if (key === "top_5_reviewers" || key === "review_top_customers" || key === "top_customer_review") {
      return { success: true, data: { items: reviewLeaderboardItems("reviewers"), display: "list" } };
    }
    const entry = getDemoUniversalKpi()?.widgets?.[key];
    if (entry) return { success: true, ...entry };
    return { success: true, data: {} };
  }

  // ── Notifications (persisted to localStorage) ─────────────────────────────
  if (path === "/notifications/team-members") {
    const members = lsReadList(DEMO_MEMBERS_KEY, () =>
      getDemoFile("notification-team-members")?.team_members ?? [],
    );
    if (m === "GET") {
      return {
        team_members: members,
        team_member_options: members.map((mem: any) => ({
          id: mem.id,
          name: mem.name,
          email: mem.email,
          whatsapp: mem.whatsapp,
          role: mem.role,
          email_verified_at: mem.email ? "2025-11-15T08:30:00.000Z" : null,
        })),
      };
    }
    if (m === "POST") {
      const b = parseBody(body);
      const newMember = {
        id: Date.now(),
        recipient_key: `rcpt_${Date.now()}`,
        user_id: b.member_id ?? null,
        merchant_id: 501,
        name: b.name ?? "New Recipient",
        email: b.email ?? null,
        whatsapp: b.whatsapp ?? null,
        slack: b.slack ?? null,
        role: "Member",
        email_send_count: 0,
        email_open_count: 0,
        whatsapp_send_count: 0,
        whatsapp_read_count: 0,
        template: b.email_template ?? "ceo",
        email_template: b.email_template ?? "ceo",
        whatsapp_template: b.whatsapp_template ?? "ceo",
        notify_via_email: !!b.use_email,
        notify_via_whatsapp: !!b.use_whatsapp,
        notify_via_slack: !!b.use_slack,
        status: "active",
        frequency: Array.isArray(b.frequency) ? b.frequency.join(",") : "daily",
        notify_status: "active",
      };
      lsWriteList(DEMO_MEMBERS_KEY, [newMember, ...members]);
      return { success: true, team_member: newMember };
    }
    return { success: true };
  }
  if (path.startsWith("/notifications/team-members/")) {
    const id = path.split("/").pop();
    const members = lsReadList(DEMO_MEMBERS_KEY, () =>
      getDemoFile("notification-team-members")?.team_members ?? [],
    );
    if (m === "DELETE") {
      lsWriteList(DEMO_MEMBERS_KEY, members.filter((mem: any) => String(mem.id) !== String(id)));
      return { success: true };
    }
    if (m === "PUT" || m === "PATCH") {
      const b = parseBody(body);
      lsWriteList(
        DEMO_MEMBERS_KEY,
        members.map((mem: any) =>
          String(mem.id) === String(id)
            ? {
                ...mem,
                ...(b.name != null ? { name: b.name } : {}),
                ...(b.email !== undefined ? { email: b.email } : {}),
                ...(b.whatsapp !== undefined ? { whatsapp: b.whatsapp } : {}),
                ...(b.slack !== undefined ? { slack: b.slack } : {}),
                ...(b.use_email != null ? { notify_via_email: !!b.use_email } : {}),
                ...(b.use_whatsapp != null ? { notify_via_whatsapp: !!b.use_whatsapp } : {}),
                ...(b.use_slack != null ? { notify_via_slack: !!b.use_slack } : {}),
                ...(b.frequency != null ? { frequency: Array.isArray(b.frequency) ? b.frequency.join(",") : b.frequency } : {}),
                ...(b.notify_status != null ? { notify_status: b.notify_status } : {}),
              }
            : mem,
        ),
      );
      return { success: true };
    }
    return { success: true };
  }
  if (path === "/notification-logs") return getDemoFile("notification-logs");
  if (path === "/notifications") return getDemoFile("notifications");

  // ── Verification / OTP ────────────────────────────────────────────────────
  // status:false so the OTP modal shows for newly added contacts; verify accepts
  // ANY 6-digit code (demo). send/resend always succeed.
  if (path === "/verification/status") return { verified: false };
  if (path === "/verification/send-otp") return { success: true, expiresIn: 300 };
  if (path === "/verification/resend-otp") return { success: true, expiresIn: 300 };
  if (path === "/verification/verify-otp") return { verified: true };

  // ── Alerts ────────────────────────────────────────────────────────────────
  if (path === "/alerts") {
    const rules = lsReadList(DEMO_ALERTS_KEY, () => getDemoFile("alerts")?.rules ?? []);
    const templates = getDemoFile("alerts")?.templates ?? [];
    if (m === "GET") return { rules, templates };
    if (m === "PUT") {
      // saveAlertRules sends the full list back.
      const b = parseBody(body);
      if (Array.isArray(b?.rules)) lsWriteList(DEMO_ALERTS_KEY, b.rules);
      return { success: true };
    }
    if (m === "POST") {
      const b = parseBody(body);
      const rule = { id: `demo-${Date.now()}`, is_active: true, ...(b?.rule ?? {}) };
      lsWriteList(DEMO_ALERTS_KEY, [rule, ...rules]);
      return { rule, success: true };
    }
  }
  if (/^\/alerts\/[^/]+$/.test(path) && m === "DELETE") {
    const id = path.split("/").pop();
    const rules = lsReadList(DEMO_ALERTS_KEY, () => getDemoFile("alerts")?.rules ?? []);
    lsWriteList(DEMO_ALERTS_KEY, rules.filter((r: any) => String(r.id) !== String(id)));
    return { success: true };
  }
  if (path === "/alerts/form-config") return getDemoFile("alert-form-config");
  if (path === "/alerts/team-members") return getDemoFile("alert-team-members");
  if (path === "/alerts/meta-accounts") {
    const fb = getDemoFile("facebook-account");
    const acc = fb?.account;
    return {
      meta_accounts: acc
        ? [
            {
              id: String(acc.id ?? acc.account_id ?? "1"),
              account_name: acc.account_name ?? acc.name ?? "Meta Ad Account",
              account_id: String(acc.account_id ?? acc.id ?? "act_0000"),
              account_alias: acc.account_alias ?? acc.name ?? "Meta",
              access_token: "demo",
            },
          ]
        : [],
    };
  }
  if (path === "/alerts/google-accounts") {
    const g = getDemoFile("google-ad-account");
    const accounts = Array.isArray(g?.accounts) ? g.accounts : [];
    return {
      google_ads_accounts: accounts.map((a: any) => ({
        id: String(a.id ?? a.google_account_id ?? a.account_id ?? "1"),
        account_name: a.account_name ?? a.name ?? "Google Ads Account",
        account_id: String(a.account_id ?? a.google_account_id ?? a.id ?? "0"),
      })),
    };
  }
  if (path === "/alerts/store-currency") return { currency: "GBP" };

  // ── User Profile / Team (localStorage-backed CRUD, unlimited members) ──────
  // Members list — seeded from team-members.json, then persisted locally.
  if (/^\/stores\/[^/]+\/members$/.test(path) && m === "GET") {
    const members = lsReadList(DEMO_TEAM_KEY, () => getDemoFile("team-members")?.members ?? []);
    return { members };
  }
  // Invitations are unused in demo (Add Member creates a real member directly),
  // so always return an empty list.
  if (/^\/stores\/[^/]+\/invitations$/.test(path)) return { invitations: [] };
  // Force the invite flow down the /invitations/send path (email never "exists"
  // with a Shopify store in demo), so Add Member always creates a member.
  if (path === "/users/validate-email" && m === "POST")
    return { exists: false, hasShopifyStore: false, userId: null };
  if (path === "/users/validate-phone" && m === "POST")
    return { exists: false, hasShopifyStore: false, userId: null };
  // Add Member → append a new member to the local team list (unlimited).
  if (path === "/invitations/send" && m === "POST") {
    const members = lsReadList<any>(DEMO_TEAM_KEY, () => getDemoFile("team-members")?.members ?? []);
    const email = (b?.email ?? "").trim();
    const phone = (b?.phone_number ?? "").trim();
    const rawPerms: string[] = Array.isArray(b?.permissions) ? b.permissions : [];
    const permsObj: Record<string, boolean> = {};
    rawPerms.forEach((p) => { permsObj[p] = true; });
    const nameFromEmail = email ? email.split("@")[0].replace(/[._-]+/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()) : "New Member";
    const newMember = {
      id: Date.now(),
      name: nameFromEmail,
      email: email || phone || "member@demo.local",
      role: b?.role ?? "member",
      is_owner: false,
      permissions: permsObj,
    };
    lsWriteList(DEMO_TEAM_KEY, [...members, newMember]);
    return { success: true, member: newMember };
  }
  // Add existing user (alternate invite path) → also append a member.
  if (path === "/stores/add-user" && m === "POST") {
    const members = lsReadList<any>(DEMO_TEAM_KEY, () => getDemoFile("team-members")?.members ?? []);
    const rawPerms: string[] = Array.isArray(b?.permissions) ? b.permissions : [];
    const permsObj: Record<string, boolean> = {};
    rawPerms.forEach((p) => { permsObj[p] = true; });
    const newMember = {
      id: Date.now(),
      name: "New Member",
      email: "member@demo.local",
      role: b?.role ?? "member",
      is_owner: false,
      permissions: permsObj,
    };
    lsWriteList(DEMO_TEAM_KEY, [...members, newMember]);
    return { success: true, member: newMember };
  }
  // Edit member role/permissions.
  const permUpdateMatch = path.match(/^\/permissions\/stores\/[^/]+\/members\/([^/]+)$/);
  if (permUpdateMatch && (m === "PUT" || m === "POST")) {
    const memberId = permUpdateMatch[1];
    const members = lsReadList<any>(DEMO_TEAM_KEY, () => getDemoFile("team-members")?.members ?? []);
    const updated = members.map((mem: any) =>
      String(mem.id) === String(memberId)
        ? {
            ...mem,
            role: b?.selectedRole ?? mem.role,
            permissions: b?.permissions ?? mem.permissions,
          }
        : mem
    );
    lsWriteList(DEMO_TEAM_KEY, updated);
    return { success: true };
  }
  if (/^\/invitations\/[^/]+\/(cancel|resend)$/.test(path))
    return { success: true };
  // Remove member from the local team list.
  const removeUserMatch = path.match(/^\/merchant\/users\/([^/]+)$/);
  if (removeUserMatch && m === "DELETE") {
    const memberId = removeUserMatch[1];
    const members = lsReadList<any>(DEMO_TEAM_KEY, () => getDemoFile("team-members")?.members ?? []);
    lsWriteList(DEMO_TEAM_KEY, members.filter((mem: any) => String(mem.id) !== String(memberId)));
    return { success: true };
  }
  if (/^\/stores\/[^/]+\/switch$/.test(path)) return { success: true };

  // ── Integrations / Connections ────────────────────────────────────────────
  if (path === "/integrations/connections-with-statuses")
    return getDemoFile("integrations-connections-with-statuses");
  if (path === "/integrations/statuses")
    return getDemoFile("integrations-statuses");
  if (path === "/integrations/contact-requests")
    return getDemoFile("integrations-contact-requests");
  if (path === "/webhook-credentials")
    return getDemoFile("webhook-credentials");

  // ── Facebook / Google account lookups used across the app ─────────────────
  if (path === "/facebook/account") return getDemoFile("facebook-account");
  if (path === "/googleAd/account") return getDemoFile("google-ad-account");
  if (path === "/googleAd/accounts") return getDemoFile("google-ad-account");

  // No mapping.
  return undefined;
}

/**
 * Entry point used by the axios request interceptor.
 * Returns a demo payload (any, including null) when demo mode is active AND the
 * request has a demo mapping. Returns the symbol `DEMO_NO_MATCH` otherwise so
 * callers can distinguish "no demo data" from "demo data is intentionally null".
 */
export const DEMO_NO_MATCH = Symbol("DEMO_NO_MATCH");

export function resolveDemoResponse(
  method: string | undefined,
  url: string | undefined,
  body?: any,
  params?: Record<string, any>,
): any {
  if (!isDemoMode()) return DEMO_NO_MATCH;
  const path = normalizePath(url || "");
  const query = parseQueryParams(url || "", params);
  const payload = mapRequestToDemo(method || "GET", path, body, query);
  if (payload === undefined) return DEMO_NO_MATCH;
  return payload;
}
